import {MultiUndirectedGraph} from "graphology";
import subgraph from "graphology-operators/subgraph"
import union from "graphology-operators/union"
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {SigmaContainer, useSigmaContext} from "@react-sigma/core";
import {SigmaEdgeGraphLoader, useEventHandlers} from "./SigmaEdgeGraphLoader";
import useSelectionRectangle from "./graph/sigma/SelectionRectangle";
import {drawNodeLabel, drawHoveredNodeLabel} from "./graph/sigma/drawNodeLabel";
import {drawHoveredEdgeLabel} from "./graph/sigma/drawEdgeLabel";
import {smartTruncate} from "../lib/utils";
import NoOpNodeProgram from "./graph/sigma/NoOpNodeProgram";
import {getTheme, THEMES} from "../lib/themeManager";

import {useElementSize} from "../hooks/useElementSize";
import Analytics from "../lib/Analytics";

// How much space for non-selected nodes to use at top/bottom of column (in pixels)
const GRAPH_MARGIN = 15;
const UPSCALE = 1000;
const ANIMATION_DURATION = 1000

export const DEFAULT_LABEL_SPACING = 16
export const DEFAULT_NODE_SIZE = 4;

// Average character width in pixels for label font (approximate for monospace-like rendering)
const AVG_CHAR_WIDTH_PIXELS = 7;  // Typical for 12-14px sans-serif font

// Default label character limits for initial truncation (used before dynamic calculation)
const DEFAULT_LABEL_LENGTH_LEFT = 30;    // Left column default
const DEFAULT_LABEL_LENGTH_MIDDLE = 30;  // Middle columns default
const DEFAULT_LABEL_LENGTH_RIGHT = 29;   // Right column default (30% increase from 22)

// Default label character limits for single-column layout
const SINGLE_COLUMN_LABEL_LENGTH = 40;   // Generous default when only one column visible

// Calculate max label length based on available pixel width
// Takes the distance between columns in pixels and returns estimated max character count
// isRHS: if true, use less conservative calculation for right-hand side labels
const calculateMaxLabelLength = (pixelWidth, isRHS = false) => {
  // RHS labels: use less conservative padding and char width for more space
  // Other columns: use conservative padding to ensure breathing room
  const padding = isRHS ? 60 : 100;  // RHS uses less padding
  const charWidthMultiplier = isRHS ? 1.0 : 1.3;  // RHS uses actual char width, others are conservative

  const usableWidth = Math.max(0, pixelWidth - padding);
  return Math.max(10, Math.floor(usableWidth / (AVG_CHAR_WIDTH_PIXELS * charWidthMultiplier)));
};

const NODE_COLOR_DEFAULT = "#e4762f";
const NODE_COLOR_SELECTED = "#e54028";
const NODE_COLOR_HOVER = "#f7be33";
const NODE_COLOR_FADE = "#F0E0E0";
const NODE_COLOR = {
  compound: "#00A000",
  similarCompound: "#00D000",
  target: "#0000FF",
  disease: "#FF0000",
  faers: "#FF7070",
  publication: "#808080",
  patent: "#C0C0C0",
  geneset: "#C000C0",
  moa: "#FFD020",
  org: "#404040",
};
const fadeColor = (c) => {
  const hex = (x) => `${x < 16 ? '0' : ''}${x.toString(16)}`;
  const bump = (x) => hex(Math.floor((x + 2 * 0xFF) / 3));
  return `#${[1, 3, 5].map(x => bump(parseInt(c.slice(x, x + 2), 16))).join("")}`;
};

// Categories that are treated as equivalent for column placement purposes.
// e.g. "faers" nodes are a disease subcategory and belong in the target column
// when targetCategory === "disease".
const CATEGORY_FAMILY = {
  disease: new Set(["disease", "faers"]),
};

const EDGE_COLOR_DEFAULT = "#C0C0D0";
const EDGE_COLOR_SELECTED = "#882014";
const EDGE_COLOR_HOVER = "#C09055";
const EDGE_COLOR_FADE = "#E0E0F0";
const EDGE_COLOR_DIRECT = "#000000";       // Black for direct edges
const EDGE_COLOR_INDIRECT = "#00CED1";     // Blue-green (cyan) for indirect edges
const EDGE_SIZE_HOVER = 6;
const EDGE_SIZE_SELECTED = 4;
const EDGE_SIZE_INDIRECT = 2;              // Thinner size for indirect edges
const MAX_INDIRECT_EDGES_PER_HUB = 5000;   // Cap predecessor*successor fan-out per collapsed-column hub node (PLEX-6623)
const EDGE_OPACITY_DIRECT = 0.08;          // Direct edges at 8% opacity (subtle, less pronounced than labels)
const EDGE_OPACITY_INDIRECT = 0.05;        // Indirect edges at 5% opacity (very subtle presence)
const NODE_OPACITY_DISCONNECTED = 0.5;     // Opacity for nodes without visible connections

// Utility function to apply opacity to hex colors
const applyOpacity = (hexColor, opacity) => {
  if (!hexColor || hexColor.startsWith('rgba')) return hexColor;
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};


// ============================================================================
// DECLARATIVE GRAPH STATE ARCHITECTURE
// ============================================================================
//
// The graph visualization uses immutable state snapshots to drive all rendering.
// Animation is achieved by interpolating between two states (A → B).
//
// GraphState: Immutable snapshot describing what the graph should look like
// - hiddenColumns: Set<columnIndex> - which columns are hidden
// - visibleNodesPerColumn: Map<columnIndex, Set<nodeId>> - filtered nodes (absent = no filter, present = active filter)
// - nodeAggregates: Map<nodeId, {count, nodeIds}> - spatial clustering results
// - columnLabelWidths: {left, middle, right} - computed max label lengths per column position
// - scaledRanks: Map<nodeId, number> - normalized ranks [0,1] for filtered nodes
//
// Key Principle: All visual properties (size, label, position) are PURE FUNCTIONS
// of GraphState. No imperative flags or suppression - just snapshot → appearance.
//
// Animation Flow:
// 1. Capture State A (before change)
// 2. Compute State B (after change) - includes new aggregates
// 3. Interpolate visual properties from A to B over time
// 4. State B becomes stable (no more interpolation)
//
// Benefits:
// - No suppression flags or special cases
// - Predictable: same state → same appearance
// - Testable: pure functions
// - No double recalculation issues
//
// ============================================================================
// CRITICAL COORDINATE SYSTEM INFORMATION
// ============================================================================
//
// ⚠️  SIGMA Y-AXIS IS INVERTED RELATIVE TO SCREEN Y-AXIS ⚠️
//
// GRAPH Y vs VIEWPORT Y vs SCREEN Y:
// - Graph Y coordinates: LOW values = top of graph, HIGH values = bottom
// - Viewport pixel Y coordinates: LOW values = top of screen, HIGH values = bottom
// - HOWEVER: Sigma's graphToViewport() transformation INVERTS the Y-axis!
//   * Small graph Y (top) → Large viewport Y (bottom)
//   * Large graph Y (bottom) → Small viewport Y (top)
// - ALWAYS convert to viewport coordinates AND sort by pixelY when processing nodes
//   top-to-bottom for label placement or any spatial calculations
//
// ⚠️  CRITICAL FOR ANIMATIONS: When moving nodes off-screen vertically:
//   * To slide nodes UP (off top of screen): use NEGATIVE graph Y (e.g., -yRange * 0.2)
//   * To slide nodes DOWN (off bottom of screen): use HIGH graph Y (e.g., yRange * 1.2)
//   * Graph coordinates are intuitive: low Y = top, high Y = bottom
//
// NODE DATA STRUCTURE:
// - node.id: Unique identifier
// - node.rank: Original rank from server (0-1, higher = better match)
// - node.column: Column index (0 = leftmost query, columnCount-1 = rightmost target)
// - node.rankNeighbors: Count of OTHER nodes sharing same rank IN SAME COLUMN (excludes self)
//   * Calculated per-column to avoid global rank collision artifacts
//   * Used for label movement decisions, NOT for node sizing
// - node.title: Full original label text (used for display)
// - node.label: Truncated label for initial rendering
//
// LABEL LAYOUT ALGORITHM (Cluster Labeling):
// 1. Process each column independently
// 2. Convert ALL node graph Y → viewport pixelY, then sort by pixelY ascending
// 3. Process nodes top-to-bottom (low pixelY to high pixelY)
// 4. Label first node, then for each subsequent node:
//    - labelSpacing is the midpoint-to-midpoint distance between label centers (default 16px)
//    - If gap >= labelSpacing: label it, start new cluster
//    - If gap < labelSpacing: add to current cluster
//      * If new node has higher rankNeighbors AND gap <= 9px AND nodes are connected:
//        move label to new node (prevents label movement to disconnected nodes)
//      * Update lastLabeledPixelY to new label position for future gap calculations
// 5. Cluster count = all nodes from first labeled node to current node (cumulative)
// 6. Aggregate count is stored in GraphState.nodeAggregates
//    - Recomputed by computeGraphState() whenever visibility/filtering changes
//    - Pure function - no side effects or refs
//
// NODE SIZING ALGORITHM:
// - Base size: 4px (DEFAULT_NODE_SIZE) for all single nodes
// - Aggregate nodes (count > 1): size = baseSize * (1.0 + log2(count) * 0.5)
//   * log2 scaling provides moderate growth: 2 nodes → 1.5x, 16 → 3.0x, 128 → 4.5x
//   * Multiplier of 0.5 prevents large aggregates from dominating visualization
//   * Capped at 20px maximum to maintain balance
//   * Size directly reflects aggregateCount (spatial cluster), NOT rankNeighbors
//   * Ensures all nodes with same aggregate count have same size across columns
// - Minimum size calculated to fit label digits (3px per digit + 4px padding)
//
// ============================================================================
// ANIMATION SYSTEM
// ============================================================================
//
// All animations interpolate between State A (before) → State B (after) over 3 seconds
// using cubic ease-out.
//
// ANIMATION RULES (determine what animates):
//
// Node Position (animates throughout FULL duration 0.0 → 1.0):
//   - Appearing nodes (not in A, in B): Start from center/off-screen → animate to B position
//   - Disappearing nodes (in A, not in B): Start from A position → animate to center/off-screen
//   - Moving nodes (in both A and B): Animate from A position → B position
//
// Node Size (animates throughout FULL duration 0.0 → 1.0):
//   - Appearing nodes: Start at size 0 → animate to B aggregate size
//   - Disappearing nodes: Start at A aggregate size → animate to size 0
//   - Moving nodes: Animate from A aggregate size → B aggregate size
//
// Node Opacity (animates throughout FULL duration 0.0 → 1.0):
//   - Appearing nodes: Fade in 0 → 1
//   - Disappearing nodes: Fade out 1 → 0
//   - Moving nodes: Stay at opacity 1.0
//
// Node Labels (fade during HALF duration):
//   - Appearing nodes: Fade in during second half (0.5 → 1.0)
//   - Disappearing nodes: Fade out during first half (0.0 → 0.5)
//   - Moving nodes where label TEXT changes: Cross-fade (1 → 0 → 1), switch text at opacity 0.5
//   - Moving nodes where label TEXT is identical: No fade (opacity stays 1.0)
//
// Edge Opacity (fade during HALF duration):
//   - Direct edges to appearing nodes: Fade in during second half (0.5 → 1.0)
//   - Direct edges to disappearing nodes: Fade out during first half (0.0 → 0.5)
//   - Indirect edges appearing (column hidden): Fade in during second half (0.5 → 1.0)
//   - Indirect edges disappearing (column shown): Fade out during first half (0.0 → 0.5)
//
// CLUSTER LABELING (which columns need label recomputation):
//   - Run when nodes are appearing in a column (column show, reset from drag-select)
//   - Run when nodes are disappearing from a column (column hide, drag-select filter)
//   - Skip (reuse State A aggregates) when all nodes exist in both states (just moving)
//
// LABEL CROSS-FADE (which columns need label text animation):
//   - When cluster labeling runs (new label assignments)
//   - When column label widths change (different truncation lengths)
//
// CRITICAL TIMING:
// - Node position/size/opacity: throughout full animation (0.0 → 1.0)
// - Edge fade-out: during first half (0.0 → 0.5)
// - Edge fade-in: during second half (0.5 → 1.0)
// - Label fade-out: during first half (0.0 → 0.5)
// - Label fade-in: during second half (0.5 → 1.0)
// - Label width switch: at opacity 0.5 (State A → State B widths, at midpoint)
//
// ============================================================================

// ============================================================================
// GraphState: Immutable state snapshot describing what the graph looks like
// ============================================================================
// This is the ONLY source of truth for graph appearance.
// All queries about visibility, filtering, aggregation go through GraphState.
class GraphState {
  constructor({
    hiddenColumns = new Set(),
    visibleNodesPerColumn = new Map(),
    nodeAggregates = new Map(),
    columnLabelWidths = { left: 30, middle: 30, right: 30 },
    scaledRanks = new Map(),
    filteredIndexRanges = new Map(),
  }) {
    this.hiddenColumns = hiddenColumns;  // Set<columnIndex> - which columns are hidden via column toggle
    this.visibleNodesPerColumn = visibleNodesPerColumn;  // Map<columnIndex, Set<nodeId>> - drag-select filtered nodes (absent = no filter, present = active filter)
    this.nodeAggregates = nodeAggregates;  // Map<nodeId, {count, nodeIds}> - spatial clustering results
    this.columnLabelWidths = columnLabelWidths; // {left, middle, right} - max label character counts
    this.scaledRanks = scaledRanks;  // Map<nodeId, number> - normalized ranks [0,1] for drag-select filtered nodes
    this.filteredIndexRanges = filteredIndexRanges;  // Map<columnIndex, {filteredNodeIds: Set<nodeId>}> - drag-select filter data
  }

  // Get set of columns with active filters
  getFilteredColumns() {
    return new Set(this.visibleNodesPerColumn.keys());
  }

  // Get aggregate info for a node (defaults to single node if not aggregated)
  getAggregate(nodeId) {
    return this.nodeAggregates.get(nodeId) || { count: 1, nodeIds: [nodeId] };
  }

  // Get scaled rank for a node (from filter normalization, or null if not filtered)
  getScaledRank(nodeId) {
    return this.scaledRanks.get(nodeId);
  }

  // Check if node should be visible in this state
  isNodeVisible(node) {
    if (!node) {
      return false; // Node not found - treat as not visible
    }
    if (this.hiddenColumns.has(node.column)) {
      return false; // Column is hidden
    }
    const nodesInColumn = this.visibleNodesPerColumn.get(node.column);
    if (!nodesInColumn) {
      return true; // No entry = no filter = all nodes visible
    }
    return nodesInColumn.has(node.id); // Check if node is in filtered set
  }

  // Check if column is visible
  isColumnVisible(column) {
    return !this.hiddenColumns.has(column);
  }

  // Check if column has an active filter
  isColumnFiltered(column) {
    return this.visibleNodesPerColumn.has(column);
  }

  // Get filtered index ranges (no recalculation, just returns stored ranges)
  // Ranges are calculated once in onSelectionEnd and stored in GraphState
  getFilteredIndexRanges() {
    return this.filteredIndexRanges;
  }
}

// ============================================================================
// ViewState: User interaction + animation state
// ============================================================================
// Tracks user interactions (hover, select, drag) and animation interpolation.
// Does NOT track graph structure - that's GraphState's job.
class ViewState {
  constructor({
    // User interaction state
    hoveredNodeId = null,
    selectedNodeIds = new Set(),
    draggedNodeId = null,
    hoveredEdgeId = null,
    selectedEdgeIds = new Set(),

    // Animation snapshots: State A → State B
    previousGraphState = null,  // Where we're animating FROM
    targetGraphState = null,    // Where we're animating TO

    // Per-frame interpolated values (updated during animation loop)
    animatedNodeSizes = new Map(),      // Map<nodeId, number>
    animatedNodeOpacities = new Map(),  // Map<nodeId, number>
    animatedEdgeOpacities = new Map(),  // Map<edgeId, number>
    animatedLabelOpacities = new Map(), // Map<nodeId, number> - per-node label fade
    animationProgress = 0,              // Current animation progress 0-1 (for cross-fade timing)

    // UI state
    isSelecting = false,
  }) {
    this.hoveredNodeId = hoveredNodeId;
    this.selectedNodeIds = selectedNodeIds;
    this.draggedNodeId = draggedNodeId;
    this.hoveredEdgeId = hoveredEdgeId;
    this.selectedEdgeIds = selectedEdgeIds;

    this.previousGraphState = previousGraphState;
    this.targetGraphState = targetGraphState;

    this.animatedNodeSizes = animatedNodeSizes;
    this.animatedNodeOpacities = animatedNodeOpacities;
    this.animatedEdgeOpacities = animatedEdgeOpacities;
    this.animatedLabelOpacities = animatedLabelOpacities;
    this.animationProgress = animationProgress;

    this.isSelecting = isSelecting;
  }

  // Check if node is directly interacted with (not just connected)
  isNodeInteractive(nodeId) {
    return this.draggedNodeId === nodeId ||
           this.hoveredNodeId === nodeId ||
           this.selectedNodeIds.has(nodeId);
  }

  // Check if we're currently animating
  isAnimating() {
    return this.previousGraphState !== null && this.targetGraphState !== null;
  }
}

// ============================================================================
// computeGraphState: Pure function to calculate complete graph state
// ============================================================================
// Given inputs (nodes, visibility, dimensions), compute all derived state:
// - Which nodes should show labels (cluster labeling)
// - Which nodes are aggregated (spatial clustering)
// - Label width limits per column
//
// This is a PURE function - same inputs always give same outputs.
// No refs, no side effects (except debug logging), fully testable.
function computeGraphState({
  columns,
  hiddenColumns,
  filteredIndexRanges,  // Map<columnIndex, {rangeStart, rangeEnd}> - source of truth for filtering
  labelSpacing,
  sigma,
  graph,
  width,
  height,
  yMultiplier,
  previousState = null,  // Previous GraphState for optimization
  labelSpacingChanged = false,  // Force recalculation when labelSpacing parameter changed
  preservedAggregates = null,  // Click-preserved hover aggregates to maintain during filtering
}) {
  // Helper: Get filtered nodes for a column, respecting filtered node IDs from drag-select
  const getFilteredNodesInColumn = (column, filteredData) => {
    if (!filteredData || !filteredData.filteredNodeIds) {
      return column.nodes; // No filter - all nodes
    }
    return column.nodes.filter(n => filteredData.filteredNodeIds.has(n.id));
  };

  // Derive filtered columns from ranges (columns with active drag-select filters)
  const filteredColumns = new Set(filteredIndexRanges.keys());

  // Build visibleNodesPerColumn from filteredColumns
  // SEMANTIC: absent entry = no drag-select filter (all nodes visible), present entry = active filter
  // Maps column index to Set of node IDs that passed the drag-select filter
  const visibleNodesPerColumn = new Map();
  filteredColumns.forEach(colIndex => {
    const col = columns[colIndex];
    if (!col || hiddenColumns.has(colIndex)) return;

    const visibleInColumn = new Set();
    const filteredNodes = getFilteredNodesInColumn(col, filteredIndexRanges.get(colIndex));
    filteredNodes.forEach(n => visibleInColumn.add(n.id));
    visibleNodesPerColumn.set(colIndex, visibleInColumn);
  });

  // Compute scaled ranks for filtered nodes
  // ONLY scale ranks when all nodes have the same rank (need to spread evenly by index)
  // Otherwise, preserve original ranks to prevent unwanted position changes during filtering
  const scaledRanks = new Map();
  visibleNodesPerColumn.forEach((visibleNodeIds, colIndex) => {
    const col = columns[colIndex];
    const filteredNodes = getFilteredNodesInColumn(col, filteredIndexRanges.get(colIndex));

    if (filteredNodes.length === 0) return;

    const minRank = Math.min(...filteredNodes.map(n => n.rank));
    const maxRank = Math.max(...filteredNodes.map(n => n.rank));
    const rankRange = maxRank - minRank;

    if (rankRange === 0) {
      // All nodes have same rank - spread evenly by index
      filteredNodes.forEach((n, idx) => {
        const scaledRank = filteredNodes.length > 1 ? idx / (filteredNodes.length - 1) : 0.5;
        scaledRanks.set(n.id, scaledRank);
      });
    } else {
      // Nodes have different ranks - scale them proportionally to 0-1 range
      filteredNodes.forEach((n, idx) => {
        const scaledRank = (n.rank - minRank) / rankRange;
        scaledRanks.set(n.id, scaledRank);
      });
    }
  });

  // Calculate visible columns (not hidden)
  const visibleColumns = columns
    .filter(col => !hiddenColumns.has(col.index))
    .map(col => col.index);

  // Calculate label widths based on column positions
  const usableWidth = width - 40; // Account for stagePadding
  let columnLabelWidths = {
    left: DEFAULT_LABEL_LENGTH_LEFT,
    middle: DEFAULT_LABEL_LENGTH_MIDDLE,
    right: DEFAULT_LABEL_LENGTH_RIGHT,
  };

  // Only calculate dynamic widths if container is large enough (width > 400px)
  // On first render, width might be 0 or very small, causing incorrect calculations
  if (visibleColumns.length > 1 && width > 400) {
    const columnSpacing = usableWidth / (visibleColumns.length - 1);
    const calculateMaxLabelLength = (pixelWidth, isRHS = false) => {
      const padding = isRHS ? 60 : 100;
      const charWidthMultiplier = isRHS ? 1.0 : 1.3;
      const usableWidth = Math.max(0, pixelWidth - padding);
      return Math.max(10, Math.floor(usableWidth / (AVG_CHAR_WIDTH_PIXELS * charWidthMultiplier)));
    };

    if (visibleColumns.length === 2) {
      const lhsToRhsPixels = usableWidth * (0.72 / 1.35) * 1.43;
      const rhsToEdgePixels = usableWidth * ((1.35 - 0.72) / 1.35) * 1.1;
      columnLabelWidths = {
        left: calculateMaxLabelLength(lhsToRhsPixels),
        middle: calculateMaxLabelLength(lhsToRhsPixels),
        right: calculateMaxLabelLength(rhsToEdgePixels, true),
      };
    } else {
      const rhsPixelDistance = columnSpacing * 0.58;
      columnLabelWidths = {
        left: calculateMaxLabelLength(columnSpacing),
        middle: calculateMaxLabelLength(columnSpacing),
        right: calculateMaxLabelLength(rhsPixelDistance, true),
      };
    }
  }

  // Compute rank neighbors per column
  const rankCountMapByColumn = new Map();
  columns.forEach(col => {
    if (hiddenColumns.has(col.index)) return;

    const columnRankMap = new Map();
    const filteredNodes = getFilteredNodesInColumn(col, filteredIndexRanges.get(col.index));
    filteredNodes.forEach(n => {
      const rank = scaledRanks.get(n.id) ?? n.rank;
      columnRankMap.set(rank, (columnRankMap.get(rank) || 0) + 1);
    });
    rankCountMapByColumn.set(col.index, columnRankMap);
  });

  // Set rankNeighbors on nodes (mutation for convenience - could be avoided)
  columns.forEach(col => {
    if (hiddenColumns.has(col.index)) return;

    const columnRankMap = rankCountMapByColumn.get(col.index);
    const filteredNodes = getFilteredNodesInColumn(col, filteredIndexRanges.get(col.index));
    filteredNodes.forEach(n => {
      const rank = scaledRanks.get(n.id) ?? n.rank;
      const count = columnRankMap ? (columnRankMap.get(rank) || 1) : 1;
      n.rankNeighbors = count > 1 ? count - 1 : undefined;
    });
  });

  // Cluster labeling per column with spatial clustering
  const aggregation = new Map();

  // Determine which columns need cluster labeling based on node appearance/disappearance
  // Cluster labeling must run when nodes are appearing or disappearing in a column
  // (changes spatial distribution requiring new label assignments)
  const columnsNeedingClusterLabeling = new Set();

  if (previousState) {
    // For each visible column, check if any nodes are appearing or disappearing
    visibleColumns.forEach(columnIndex => {
      const column = columns[columnIndex];
      let hasAppearingNodes = false;
      let hasDisappearingNodes = false;

      // Get filtered nodes in State B (being computed)
      const filteredInB = getFilteredNodesInColumn(column, filteredIndexRanges.get(columnIndex));
      const filteredInBSet = new Set(filteredInB.map(n => n.id));

      // Check for appearing nodes (in State B but not in State A)
      filteredInB.forEach(n => {
        const visibleInA = previousState.isNodeVisible(n);
        if (!visibleInA) {
          hasAppearingNodes = true;
        }
      });

      // Check for disappearing nodes (in State A but not in State B)
      column.nodes.forEach(n => {
        const visibleInA = previousState.isNodeVisible(n);
        const inStateB = filteredInBSet.has(n.id);

        if (visibleInA && !inStateB) {
          hasDisappearingNodes = true;
        }
      });

      // Check if the filtered node set changed (even if same nodes, different filter)
      // This handles the case where a cluster is clicked and filtered - the same nodes
      // are visible before and after, but the filtering changes their spatial distribution
      const previousFilteredNodeIds = previousState.visibleNodesPerColumn.get(columnIndex);
      const hasFilterChange = previousFilteredNodeIds !== undefined || filteredIndexRanges.has(columnIndex);

      if (hasAppearingNodes || hasDisappearingNodes || hasFilterChange) {
        columnsNeedingClusterLabeling.add(columnIndex);
      }
    });
  }

  visibleColumns.forEach(columnIndex => {
    const column = columns[columnIndex];

    // Check if we have preserved aggregates for this column (from hover → click)
    // Preserved aggregates take precedence to maintain hover-time clustering after filtering
    if (preservedAggregates && preservedAggregates.size > 0) {
      let hasPreservedForColumn = false;
      for (const [nodeId, agg] of preservedAggregates.entries()) {
        const nodeData = column.nodes.find(n => n.id === nodeId);
        if (nodeData) {
          hasPreservedForColumn = true;
          break;
        }
      }

      if (hasPreservedForColumn) {
        // Use preserved aggregates for this column
        console.log(`[ComputeGraphState] Column ${columnIndex}: using ${preservedAggregates.size} preserved aggregates`);
        for (const [nodeId, agg] of preservedAggregates.entries()) {
          const nodeData = column.nodes.find(n => n.id === nodeId);
          if (nodeData) {
            aggregation.set(nodeId, agg);
          }
        }
        return; // Skip cluster labeling for this column
      }
    }

    // Only run cluster labeling for columns with changing vertical distribution
    // For unchanged columns, reuse aggregates from previous state
    const needsClusterLabeling = columnsNeedingClusterLabeling.has(columnIndex) || labelSpacingChanged;

    if (!needsClusterLabeling && previousState) {
      // Column not changing - copy aggregates from previous state
      column.nodes.forEach(n => {
        if (previousState.nodeAggregates.has(n.id)) {
          aggregation.set(n.id, previousState.nodeAggregates.get(n.id));
        }
      });
      return; // Skip cluster labeling for this column
    }

    // Column is changing distribution - run cluster labeling
    // Calculate positions from ranks for all columns uniformly
    // Column 0 ranks are degree-based (set in columns useMemo), other columns use server ranks
    const yRange = yMultiplier(width, height) * 1.15; // Match deriveY scaling
    const filteredNodes = getFilteredNodesInColumn(column, filteredIndexRanges.get(columnIndex));
    const columnNodes = filteredNodes
      .filter(n => graph.hasNode(n.id))
      .map(n => {
        // Use scaledRanks if filtering is active, otherwise use node's rank from column data
        const rank = scaledRanks.get(n.id) ?? n.rank;
        const targetY = rank * yRange;
        return { id: n.id, y: targetY, rank };
      });

    // Sort by viewport pixel Y
    columnNodes.forEach(node => {
      const viewportPos = sigma.graphToViewport({ x: 0, y: node.y });
      node.pixelY = viewportPos.y;
    });
    columnNodes.sort((a, b) => a.pixelY - b.pixelY);

    if (columnNodes.length === 0) return;

    // Cluster labeling algorithm (greedy placement)
    const labelHeightPixels = 18;
    const requiredPixelSpacing = labelSpacing;
    let lastLabeledPixelY = -Infinity;
    let currentLabeledNodeId = null;
    let currentClusterNodes = [];

    columnNodes.forEach(({ id, y, rank, pixelY }, index) => {
      const pixelGap = pixelY - lastLabeledPixelY;
      const hasSpace = pixelGap >= requiredPixelSpacing;
      const node = column.nodes.find(n => n.id === id);
      const nodeRankNeighbors = node?.rankNeighbors || 0;

      // Debug logging for column 0 (LHS)
      if (columnIndex === 0) {
        const graphY = graph.hasNode(id) ? graph.getNodeAttribute(id, 'y') : 'N/A';
        console.log(`[ClusterLabel] Col ${columnIndex} Node ${index}: id=${id.substring(0, 30)}, calcY=${y.toFixed(1)}, graphY=${typeof graphY === 'number' ? graphY.toFixed(1) : graphY}, pixelY=${pixelY.toFixed(1)}, gap=${pixelGap.toFixed(1)}, hasSpace=${hasSpace}, spacing=${requiredPixelSpacing}, lastY=${lastLabeledPixelY.toFixed(1)}`);
      }

      if (!currentLabeledNodeId || hasSpace) {
        // Save previous cluster
        if (currentLabeledNodeId) {
          aggregation.set(currentLabeledNodeId, {
            count: currentClusterNodes.length,
            nodeIds: currentClusterNodes.map(n => n.id),
          });
        }

        // Label this node and start new cluster
        lastLabeledPixelY = pixelY;
        currentLabeledNodeId = id;
        currentClusterNodes = [columnNodes[index]];

        if (columnIndex === 0) {
          console.log(`  → LABELED (new cluster, count will be ${currentClusterNodes.length})`);
        }
      } else {
        // No space - add to current cluster
        currentClusterNodes.push(columnNodes[index]);

        if (columnIndex === 0) {
          console.log(`  → Added to cluster (current size: ${currentClusterNodes.length})`);
        }

        // Check if we should move label to this node
        const currentLabeledNodeData = column.nodes.find(n => n.id === currentLabeledNodeId);
        const currentRankNeighbors = currentLabeledNodeData?.rankNeighbors || 0;
        const maxLabelMoveDistance = labelHeightPixels / 2;

        let areConnected = false;
        if (graph.hasNode(currentLabeledNodeId) && graph.hasNode(id)) {
          try {
            const currentNeighbors = new Set(graph.neighbors(currentLabeledNodeId));
            const candidateNeighbors = new Set(graph.neighbors(id));
            areConnected = currentNeighbors.has(id) || candidateNeighbors.has(currentLabeledNodeId) ||
              Array.from(currentNeighbors).some(n => candidateNeighbors.has(n));
          } catch (e) {
            areConnected = false;
          }
        }

        if (nodeRankNeighbors > currentRankNeighbors && pixelGap <= maxLabelMoveDistance && areConnected) {
          if (columnIndex === 0) {
            console.log(`  → LABEL MOVED to this node (rankNeighbors: ${nodeRankNeighbors} > ${currentRankNeighbors}, connected: ${areConnected})`);
          }
          currentLabeledNodeId = id;
          // CRITICAL: Do NOT update lastLabeledPixelY when moving label within cluster
          // This ensures next node's gap is calculated from the first labeled node's position,
          // not from where the label ended up after movement
          // lastLabeledPixelY = pixelY;  // REMOVED - this was preventing subsequent nodes from being labeled
        } else if (columnIndex === 0) {
          console.log(`  → Label NOT moved (rankNeighbors: ${nodeRankNeighbors} vs ${currentRankNeighbors}, gap: ${pixelGap.toFixed(1)} vs max ${maxLabelMoveDistance}, connected: ${areConnected})`);
        }
      }
    });

    // Save final cluster
    if (currentLabeledNodeId) {
      aggregation.set(currentLabeledNodeId, {
        count: currentClusterNodes.length,
        nodeIds: currentClusterNodes.map(n => n.id),
      });
    }
  });

  return new GraphState({
    hiddenColumns,
    visibleNodesPerColumn,
    nodeAggregates: aggregation,
    columnLabelWidths,
    scaledRanks,
    filteredIndexRanges,
  });
}

// ============================================================================
// applyClusterLabeling: Reusable cluster labeling algorithm
// ============================================================================
// Given a list of nodes in a column (sorted by viewport pixel Y), determine which
// nodes should show labels and assign them to clusters.
// Returns a Map of nodeId -> {count, nodeIds} for nodes that should show labels.
// nodeByIdRef: Map of node ID -> node data (must be passed in from caller)
function applyClusterLabeling(columnNodes, labelSpacing, sigma, graph, nodeByIdRef, columnIndex = null) {
  if (columnNodes.length === 0) return new Map();

  const aggregation = new Map();
  const labelHeightPixels = 18;
  const requiredPixelSpacing = labelSpacing;
  let lastLabeledPixelY = -Infinity;
  let currentLabeledNodeId = null;
  let currentClusterNodes = [];

  columnNodes.forEach(({ id, y, rank, pixelY }, index) => {
    const pixelGap = pixelY - lastLabeledPixelY;
    const hasSpace = pixelGap >= requiredPixelSpacing;

    // Get node data to check rankNeighbors
    const nodeData = nodeByIdRef.current.get(id);
    const nodeRankNeighbors = nodeData?.rankNeighbors || 0;

    if (!currentLabeledNodeId || hasSpace) {
      // Save previous cluster
      if (currentLabeledNodeId) {
        aggregation.set(currentLabeledNodeId, {
          count: currentClusterNodes.length,
          nodeIds: currentClusterNodes.map(n => n.id),
        });
      }

      // Label this node and start new cluster
      lastLabeledPixelY = pixelY;
      currentLabeledNodeId = id;
      currentClusterNodes = [columnNodes[index]];
    } else {
      // No space - add to current cluster
      currentClusterNodes.push(columnNodes[index]);

      // Check if we should move label to this node
      const currentLabeledNodeData = nodeByIdRef.current.get(currentLabeledNodeId);
      const currentRankNeighbors = currentLabeledNodeData?.rankNeighbors || 0;
      const maxLabelMoveDistance = labelHeightPixels / 2;

      let areConnected = false;
      if (graph.hasNode(currentLabeledNodeId) && graph.hasNode(id)) {
        try {
          const currentNeighbors = new Set(graph.neighbors(currentLabeledNodeId));
          const candidateNeighbors = new Set(graph.neighbors(id));
          areConnected = currentNeighbors.has(id) || candidateNeighbors.has(currentLabeledNodeId) ||
            Array.from(currentNeighbors).some(n => candidateNeighbors.has(n));
        } catch (e) {
          areConnected = false;
        }
      }

      if (nodeRankNeighbors > currentRankNeighbors && pixelGap <= maxLabelMoveDistance && areConnected) {
        currentLabeledNodeId = id;
        // CRITICAL: Do NOT update lastLabeledPixelY when moving label within cluster
      }
    }
  });

  // Save final cluster
  if (currentLabeledNodeId) {
    aggregation.set(currentLabeledNodeId, {
      count: currentClusterNodes.length,
      nodeIds: currentClusterNodes.map(n => n.id),
    });
  }

  return aggregation;
}

// ============================================================================

// drag > hover > select
// drag - no highlights, fade select, hide other
// hover - node => connected highlights, edge => segment highlight, fade select, hide other
// select - node => connected highlights, edge => segment highlight, fade other

export const SigmaEdgeGraph = (props) => {
  const {
    nodes: adjacencyNodes = [],
    adjacency = [],
    columnCount = 0,
    onDetailTargetChange = null, // Callback when detail panel target changes (edge or node)
    onAnimationChange = null, // Callback for animation state changes
    similarityColumn = -1, intermediateColumn = -1,
    showSimilarity = true, showIntermediate = true,
    sortBySimilarity = false,
    labelFilter = "",
    queryCategory = "",
    labelSpacing = DEFAULT_LABEL_SPACING, // Default 8 units if not provided
    legendRef = null, // Ref to pass legend data to parent
  } = props;

  // Transform flat node list into column-oriented structure for efficient operations
  // CRITICAL: Calculate column 0 degrees from adjacency list BEFORE creating columns
  const columns = useMemo(() => {
    // Step 1: Calculate degrees for column 0 nodes from adjacency list
    const nodeIdToIndex = new Map(adjacencyNodes.map((n, idx) => [n.id, idx]));
    const degreeMap = new Map();

    // Initialize all nodes with degree 0 (in case they have no edges)
    adjacencyNodes.forEach(n => degreeMap.set(n.id, 0));

    // Count edges for each node from adjacency list
    adjacency.forEach((neighbors, sourceIdx) => {
      const sourceNode = adjacencyNodes[sourceIdx];
      if (!sourceNode) return;

      // Count outgoing edges
      const outDegree = neighbors.length;
      degreeMap.set(sourceNode.id, (degreeMap.get(sourceNode.id) || 0) + outDegree);

      // Count incoming edges
      neighbors.forEach(([targetIdx]) => {
        const targetNode = adjacencyNodes[targetIdx];
        if (targetNode) {
          degreeMap.set(targetNode.id, (degreeMap.get(targetNode.id) || 0) + 1);
        }
      });
    });

    // Step 2: Calculate degree-based ranks for column 0 (connectivity-based ranking)
    // Query terms (column 0) are ranked based on graph degree instead of server-provided rank.
    // This spreads query terms vertically based on connectivity: highly connected terms appear
    // at the top/bottom, less connected terms are distributed proportionally.
    // This prioritizes exploring the most connected query terms in the visualization.
    // Use linear normalization to preserve degree density (similar degrees → close spacing)
    const degreeBasedRanks = new Map();
    const col0Nodes = adjacencyNodes.filter(n => n.column === 0);
    if (col0Nodes.length > 0) {
      const degrees = col0Nodes.map(n => degreeMap.get(n.id) || 0);
      const maxDegree = Math.max(...degrees);
      const minDegree = Math.min(...degrees);
      const degreeRange = maxDegree - minDegree;

      col0Nodes.forEach((node) => {
        const degree = degreeMap.get(node.id) || 0;
        const degreeRank = degreeRange === 0 ? 0.5 : (degree - minDegree) / degreeRange;
        degreeBasedRanks.set(node.id, degreeRank);
      });
    }

    // Step 3: Create column-oriented structure with copies that have correct ranks
    const cols = Array.from({ length: columnCount }, (_, i) => ({
      index: i,
      nodes: []
    }));
    adjacencyNodes.forEach(node => {
      if (node.column >= 0 && node.column < columnCount) {
        // Always create copy to prevent prop mutation
        const nodeCopy = { ...node };

        // For column 0, use degree-based rank instead of original rank
        if (node.column === 0 && degreeBasedRanks.has(node.id)) {
          nodeCopy.rank = degreeBasedRanks.get(node.id);
        }

        cols[node.column].nodes.push(nodeCopy);
      }
    });

    // Step 4: Sort each column by rank to ensure array indices match spatial order
    // Sort LOW to HIGH (rank 0 = top, rank 1 = bottom)
    cols.forEach(col => {
      col.nodes.sort((a, b) => a.rank - b.rank);
    });

    return cols;
  }, [adjacencyNodes, columnCount, adjacency]);

  // Apply degree-adjusted ranks back to adjacencyNodes for graph rendering
  // CRITICAL: fullAdjacency is indexed by adjacencyNodes order, so we must preserve that order
  // for edge creation in SigmaEdgeGraphLoader, but with the corrected degree-based ranks
  const nodes = useMemo(() => {
    // Build map of node ID to degree-adjusted rank from columns
    const rankMap = new Map();
    columns.forEach(col => {
      col.nodes.forEach(node => {
        rankMap.set(node.id, node.rank);
      });
    });

    // Copy ranks onto adjacencyNodes (keeping original order for adjacency indexing)
    return adjacencyNodes.map(node => ({
      ...node,
      rank: rankMap.get(node.id) ?? node.rank, // Use adjusted rank if available
    }));
  }, [adjacencyNodes, columns]);

  // Maintain graph user interactivity state
  // NOTE: sigma won't update its callbacks, so we have to update dependencies within `state`
  // and then explicitly call refresh() when dependencies change
  const state = useMemo(() => ({
    showSimilarity,
    showIntermediate,
    sortBySimilarity,
    similarityColumn,
    intermediateColumn,
    columnCount,
    queryCategory,
    columns,  // Column-oriented structure for efficient operations
    hoveredColumn: undefined,  // Track which column has hovered nodes
  }), [columns]);
  const {sigma, container} = useSigmaContext();
  const graph = sigma.getGraph();
  const [minSimilarity, setMinSimilarity] = useState(1);
  const [maxSimilarity, setMaxSimilarity] = useState(0);
  const containerSize = useElementSize(container);
  const width = containerSize.width;
  const height = containerSize.height;

  // Define helper functions for layout calculations (needed by useMemos and callbacks)
  const xMultiplier = useCallback((w, h) => UPSCALE * (w < h ? 1 : w / (h || 1)), []);
  const yMultiplier = useCallback((w, h) => UPSCALE * (h < w ? 1 : h / (w || 1)), []);

  // ============================================================================
  // Declarative State Management
  // ============================================================================
  // Replace imperative refs with immutable GraphState snapshots

  // Current stable state (what the graph looks like when not animating)
  const [currentState, setCurrentState] = useState(null);

  // Target state for animation: when non-null, animation runs from currentState → targetState
  // Upon completion, currentState = targetState and targetState = null
  const [targetState, setTargetState] = useState(null);

  // Freeze adjacency during column hide/show animation to keep edges visible

  // Animation tracking
  const isRandomizingRef = useRef(false);  // Special case for randomize animation
  const labelOpacityRef = useRef(1.0);  // Track label opacity for fade-in/out animation
  const [isAnimating, setIsAnimating] = useState(false);  // Track animation for UI disabling

  // Fast lookup for nodes (use degree-adjusted nodes with correct ranks)
  // CRITICAL: Populate synchronously (not in useEffect) to avoid race condition
  // The nodeReducer needs this map during graph import, which happens before effects run
  const nodeByIdRef = useRef(new Map());
  nodeByIdRef.current = new Map(nodes.map(n => [n.id, n]));

  // Semantic view state - replaces imperative refs with declarative state object
  const viewStateRef = useRef(new ViewState({
    visibleNodeIds: new Set(nodes.map(n => n.id)),
  }));

  // Store renderer state on sigma instance for custom renderers to access
  // Sigma 3.x doesn't support custom settings via setSetting()
  // Instead, we attach state directly to the sigma instance

  // Track last processed column visibility props for change detection
  // (separate from ViewState to keep ViewState purely declarative)
  const prevColumnVisibilityRef = useRef({ showSimilarity: null, showIntermediate: null });

  // Track previous labelSpacing to detect when ONLY labelSpacing changed
  const prevLabelSpacingRef = useRef(labelSpacing);

  // Legacy compatibility (will be removed as we refactor)
  const isAnimatingRef = useRef(false);
  // Legacy animation refs removed - now using ViewState for declarative animation tracking
  // Legacy cluster aggregation ref removed - now using declarative currentState.nodeAggregates

  // Define helper function for ranking calculations (needed by deriveY)
  // Takes optional GraphState parameter to look up scaled ranks
  const getOrder = useCallback((d, graphState = null) => {
    if (sortBySimilarity && d.column === similarityColumn) {
      return ((d.scaledScore || d.score) - minSimilarity) / (maxSimilarity - minSimilarity);
    }

    // Try to get scaled rank from GraphState if provided
    const scaledRank = graphState?.getScaledRank(d.id);
    return scaledRank ?? d.rank;
  }, [sortBySimilarity, similarityColumn, minSimilarity, maxSimilarity]);

  const deriveY = useCallback((d, graphState) => {
    // CRITICAL: GraphState is required - never use default/fallback
    if (!graphState) {
      throw new Error('deriveY requires GraphState - received null');
    }
    // Use expanded vertical space to reduce top/bottom margins
    // Scale by 1.15 to push nodes closer to edges while keeping them within safe bounds
    return getOrder(d, graphState) * yMultiplier(width, height) * 1.15;
  }, [width, height, getOrder, yMultiplier]);

  // Drag-select filter state (source of truth for filtering)
  // Map<columnIndex, {rangeStart, rangeEnd}> - which ranges are selected
  // Empty map = no filter = all nodes visible
  const [filteredIndexRanges, setFilteredIndexRanges] = useState(new Map());

  // Remove synchronous useMemo - let React's effect system handle state transitions naturally

  // ============================================================================
  // Initialize and Update Graph State
  // ============================================================================
  // Compute fresh state whenever visibility or dimensions change
  // This is the source of truth for what the graph SHOULD look like
  useEffect(() => {
    if (!sigma || !graph || graph.order === 0) {
      return;
    }

    // Calculate which columns are hidden based on user intent
    const hiddenColumns = new Set();
    if (similarityColumn >= 0 && !showSimilarity) {
      hiddenColumns.add(similarityColumn);
    }
    if (intermediateColumn >= 0 && !showIntermediate) {
      hiddenColumns.add(intermediateColumn);
    }

    // Check if label spacing changed before computing new state
    const labelSpacingChanged = prevLabelSpacingRef.current !== labelSpacing;

    // Compute complete graph state using pure function
    const newState = computeGraphState({
      columns,
      hiddenColumns,
      filteredIndexRanges: filteredIndexRanges,
      labelSpacing,
      sigma,
      graph,
      width,
      height,
      yMultiplier,
      previousState: currentState,
      labelSpacingChanged,  // Force recalculation when labelSpacing parameter changed
      preservedAggregates: state.clickPreservedAggregates,  // Pass preserved hover-time aggregates
    });

    // Check if ONLY labelSpacing changed (no other state changes)
    const onlyLabelSpacingChanged = currentState && labelSpacingChanged;

    // Update ref for next comparison
    prevLabelSpacingRef.current = labelSpacing;

    // Trigger animation by setting targetState
    if (currentState) {
      // We have a previous state - decide whether to animate
      if (onlyLabelSpacingChanged) {
        // ONLY label spacing changed - update immediately without animation
        // Follow the hover pattern: store aggregates in state object, refresh immediately
        state.labelSpacingAggregates = newState.nodeAggregates;
        state.labelSpacingWidths = newState.columnLabelWidths;
        sigma.refresh();
        // Then update persistent state (can be async)
        setCurrentState(newState);
      } else {
        // Other changes - animate the transition
        setTargetState(newState);
      }
    } else {
      // First initialization - just set the state directly, no animation needed
      setCurrentState(newState);
      // targetState remains null - no animation
    }

    // Reset label opacity when state changes
    labelOpacityRef.current = 1.0;
  }, [nodes, filteredIndexRanges, showSimilarity, showIntermediate, similarityColumn, intermediateColumn,
      columnCount, width, height, sigma, graph, yMultiplier, columns, labelSpacing]);

  // No need to reset filtered nodes - they're derived from filteredIndexRanges

  // Compute ALL edges (direct + indirect) - no filtering by column visibility
  // edgeReducer handles visibility logic
  const fullAdjacency = useMemo(() => {
    const combined = new Array(adjacencyNodes.length).fill(null).map(() => []);

    // Add all original edges from server with metadata
    // All server edges are direct connections between adjacent columns
    adjacency.forEach((neighbors, sourceIdx) => {
      const sourceNode = adjacencyNodes[sourceIdx];
      if (!sourceNode) return;

      neighbors.forEach(([targetIdx, edgeAttrs]) => {
        const targetNode = adjacencyNodes[targetIdx];
        if (!targetNode) return;

        combined[sourceIdx].push([targetIdx, {
          ...edgeAttrs,
          // Server edges have no isIndirect flag (undefined = direct edge)
        }]);
      });
    });

    // Generate indirect edges: for each hideable column, create synthetic edges
    // that represent collapsed multi-hop paths through that column
    // These are ONLY shown when the intermediate column is hidden
    const generateIndirectEdgesForColumn = (intermediateCol) => {
      if (intermediateCol < 0) return;

      // Build adjacency map for faster lookups
      const adjacencyMap = new Map();
      adjacencyNodes.forEach((node, idx) => {
        adjacencyMap.set(node.id, { idx, neighbors: combined[idx] || [] });
      });

      // For each node in the intermediate column, create indirect edges
      // from its predecessors to its successors
      let indirectCount = 0;
      adjacencyNodes.forEach((intermediateNode, intermediateIdx) => {
        if (intermediateNode.column !== intermediateCol) return;

        // Find predecessors (nodes that connect TO this intermediate node)
        const predecessors = [];
        adjacencyNodes.forEach((srcNode, srcIdx) => {
          if (srcNode.column >= intermediateCol) return; // Only look at earlier columns
          const neighbors = combined[srcIdx] || [];
          if (neighbors.some(([targetIdx]) => targetIdx === intermediateIdx)) {
            predecessors.push({ node: srcNode, idx: srcIdx });
          }
        });

        // Find successors (nodes that this intermediate node connects TO)
        const successors = [];
        (combined[intermediateIdx] || []).forEach(([targetIdx]) => {
          const targetNode = adjacencyNodes[targetIdx];
          if (targetNode && targetNode.column > intermediateCol) {
            successors.push({ node: targetNode, idx: targetIdx });
          }
        });

        // Create indirect edges: predecessor → successor (collapsing intermediate).
        // Cap worst-case P*S fan-out per hub node (PLEX-6623: unbounded here caused
        // browser OOM on wide queries funneling through a small intermediate hub) --
        // synthetic indirect edges are a UI convenience for collapsed columns, not
        // data that must be complete.
        if (predecessors.length * successors.length > MAX_INDIRECT_EDGES_PER_HUB) {
          return;
        }
        predecessors.forEach(({ node: srcNode, idx: srcIdx }) => {
          successors.forEach(({ node: tgtNode, idx: tgtIdx }) => {
            // Check if edge already exists
            const existing = combined[srcIdx].find(([idx]) => idx === tgtIdx);
            if (existing) {
              const [, attrs] = existing;

              // If edge has isIndirect flag, it's an existing indirect edge - increment collapsed count
              // If no isIndirect flag (undefined), it's a direct edge from server - don't modify
              if (attrs.isIndirect) {
                // Existing indirect edge - increment collapsed count
                attrs.collapsedCount = (attrs.collapsedCount || 1) + 1;
              }
              // If it's a direct edge (no isIndirect flag), don't create/modify - direct path already exists
            } else {
              // No edge exists - create new indirect edge
              const newEdge = {
                // NO LABEL - indirect edges aggregate many paths and shouldn't show labels
                label: '',
                color: EDGE_COLOR_INDIRECT,
                isIndirect: true,  // ONLY flag needed - marks as synthetic indirect edge
                collapsedCount: 1,
              };
              combined[srcIdx].push([tgtIdx, newEdge]);
              indirectCount++;
            }
          });
        });
      });
    };

    // Generate indirect edges for similarity column, but only when it's actually
    // hidden (PLEX-6623: this used to run unconditionally, paying full P*S synthetic
    // edge cost even while the column was visible and the edges were unused).
    if (similarityColumn >= 0 && !showSimilarity) {
      generateIndirectEdgesForColumn(similarityColumn);
    }

    // Same for the intermediate column.
    if (intermediateColumn >= 0 && !showIntermediate) {
      generateIndirectEdgesForColumn(intermediateColumn);
    }

    return combined;
  }, [adjacency, adjacencyNodes, columnCount, similarityColumn, intermediateColumn, showSimilarity, showIntermediate]);

  // Track whether there are any direct connections from query column to target column
  // when showing indirect edges
  const hasDirectConnections = useMemo(() => {
    const leftmostIsCompound = queryCategory === "compound";
    const similarityHidden = !showSimilarity;

    if (!leftmostIsCompound || !similarityHidden || !currentState) {
      return true; // Not in indirect edge mode, don't show warning
    }

    // Check if there are any direct edges from leftmost (query) column to rightmost (target) column
    const targetColumn = columnCount - 1;
    const queryColumn = 0;

    for (let idx = 0; idx < adjacencyNodes.length; idx++) {
      const node = adjacencyNodes[idx];
      if (!node || !currentState.isNodeVisible(node)) continue;

      if (node.column === queryColumn) {
        // Check if this query node has direct connections to target column
        const neighbors = fullAdjacency[idx] || [];
        for (const [targetIdx, edgeAttrs] of neighbors) {
          const targetNode = adjacencyNodes[targetIdx];
          // Only count real direct edges (not indirect), and only if target is in target column
          if (!edgeAttrs.isIndirect && targetNode && currentState.isNodeVisible(targetNode) && targetNode.column === targetColumn) {
            return true; // Found direct query → target connection
          }
        }
      }
    }

    return false; // No direct query → target connections found
  }, [fullAdjacency, adjacencyNodes, currentState, queryCategory, showSimilarity, columnCount]);

  const deriveX = useCallback((d, graphState = null) => {
    const multiplier = xMultiplier(width, height);

    // CRITICAL: GraphState is required - never fall back to component props or node properties
    // This ensures consistent position calculation based on declarative state
    // Animation works by comparing positions from State A vs State B, so we MUST
    // calculate positions from GraphState, not from mutable node properties
    if (!graphState) {
      throw new Error('deriveX requires GraphState - received null');
    }

    const isColumnHidden = (col) => {
      return !graphState.isColumnVisible(col);
    };

    const visibleColumns = [];
    for (let col = 0; col < columnCount; col++) {
      if (!isColumnHidden(col)) {
        visibleColumns.push(col);
      }
    }

    // If there's only one visible column, center it
    if (visibleColumns.length <= 1) {
      return multiplier / 2;
    }

    // Map node's column to its visual position index
    const visualIndex = visibleColumns.indexOf(d.column);
    if (visualIndex === -1) {
      // Node is in a hidden column - calculate as if it were the rightmost visible column
      // This prevents nodes from jumping to x=0 during animation
      const visualIndex = visibleColumns.length;
      if (visibleColumns.length === 1) {
        // Will be 2 columns after this one is shown
        return multiplier * 0.72;
      }
      // Calculate position as if this column were appended
      return (visualIndex / visibleColumns.length) * multiplier;
    }

    // When only 2 node columns visible (LHS and RHS), treat as 3-column layout with phantom anchor
    // Position RHS to provide optimal label space
    if (visibleColumns.length === 2) {
      // LHS at 0, RHS at 0.72, anchor at 1.35
      // This gives RHS about 53% of viewport (0.72/1.35 ≈ 0.53)
      return visualIndex === 0 ? 0 : multiplier * 0.72;
    }

    // Normal case: 3+ node columns with anchor providing RHS label space
    // Use full width for nodes - RHS label space is created by anchor node
    return (visualIndex / (visibleColumns.length - 1)) * multiplier;
  }, [columnCount, width, height, xMultiplier]);

  // Calculate dynamic label widths based on actual pixel spacing between columns
  const columnLabelWidths = useMemo(() => {
    // Calculate visible columns
    const isColumnHidden = (col) => {
      return col === similarityColumn && !showSimilarity
             || col === intermediateColumn && !showIntermediate;
    };
    const visibleColumns = [];
    for (let col = 0; col < columnCount; col++) {
      if (!isColumnHidden(col)) {
        visibleColumns.push(col);
      }
    }

    if (visibleColumns.length <= 1) {
      // Single column - use generous default
      return {
        left: SINGLE_COLUMN_LABEL_LENGTH,
        middle: SINGLE_COLUMN_LABEL_LENGTH,
        right: SINGLE_COLUMN_LABEL_LENGTH
      };
    }

    // Calculate pixel spacing directly from container width
    const usableWidth = width - 40; // Account for stagePadding (20px on each side)

    if (visibleColumns.length === 2) {
      // Special case: when only 2 columns, RHS is at 0.72 with anchor at 1.35
      // LHS at 0, RHS at 0.72, anchor at 1.35
      // Sigma scales viewport to fit [0, 1.35], so:
      // - LHS appears at 0 / 1.35 ≈ 0% of viewport width
      // - RHS appears at 0.72 / 1.35 ≈ 53% of viewport width
      // Distance LHS→RHS = 0.72 / 1.35 ≈ 53% of viewport width
      // LHS labels get 43% more width (1.3 * 1.1), RHS labels get 10% more width
      const lhsToRhsPixels = usableWidth * (0.72 / 1.35) * 1.43;
      // Distance RHS→right edge = (1.35 - 0.72) / 1.35 ≈ 47% of viewport width
      const rhsToEdgePixels = usableWidth * ((1.35 - 0.72) / 1.35) * 1.1;

      return {
        left: calculateMaxLabelLength(lhsToRhsPixels),
        middle: calculateMaxLabelLength(lhsToRhsPixels),  // Not used in 2-column case
        right: calculateMaxLabelLength(rhsToEdgePixels, true),  // Space from RHS to right edge
      };
    }

    // Normal case: 3+ columns evenly distributed
    // Nodes are evenly distributed across the width, with anchor node at 135% extending the range
    const columnSpacing = usableWidth / (visibleColumns.length - 1);

    // RHS: distance from last column to where anchor node forces the edge (35% beyond normal range)
    // The anchor extends the graph by 35%, so RHS labels have 35% of the column spacing
    // Use more aggressive multiplier for RHS to allow more label space (30% wider than before)
    const rhsPixelDistance = columnSpacing * 0.58;

    return {
      left: calculateMaxLabelLength(columnSpacing),
      middle: calculateMaxLabelLength(columnSpacing),
      right: calculateMaxLabelLength(rhsPixelDistance, true),  // Pass true for RHS to use less conservative calculation
    };
  }, [width, height, columnCount, similarityColumn, intermediateColumn, showSimilarity, showIntermediate]);

  const onSelectionChange = useCallback(({nodes: selectedNodeIDs}) => {
    if (isAnimatingRef.current) return;
    state.selectedNodeIDs = selectedNodeIDs && selectedNodeIDs.length ? new Set(selectedNodeIDs) : undefined;
    state.selectedEdges = undefined;
    // Use scheduleRefresh for smoother updates during selection drag
    sigma.scheduleRefresh();
  }, [sigma]);

  /**
   * Calculate node size from GraphState aggregate count
   */
  const calculateNodeSize = useCallback((nodeId, graphState) => {
    const baseSize = DEFAULT_NODE_SIZE;
    if (!graphState) return baseSize;

    const node = nodeByIdRef.current.get(nodeId);
    if (node?.column === 0) {
      // Query column: scale by rank
      const minQuerySize = 8;
      const maxQuerySize = 15;
      const rank = node?.rank || 0;
      return minQuerySize + (maxQuerySize - minQuerySize) * rank;
    }

    const aggregate = graphState.getAggregate(nodeId);
    const aggregateCount = aggregate.count;

    if (aggregateCount > 1) {
      const magnitudeOrder = Math.log2(aggregateCount);
      const sizeMultiplier = 1.0 + (magnitudeOrder * 0.5);
      const labelDigits = String(aggregateCount).length;
      const minSizeForLabel = Math.max(8, 3 * labelDigits + 4);
      return Math.max(minSizeForLabel, Math.min(20, baseSize * sizeMultiplier));
    }
    return baseSize;
  }, []);

  /**
   * Build animation descriptors for State A → State B transition
   * Single-pass: compute all node animations with final sizes from State B
   */
  const buildAnimations = useCallback((stateA, stateB, yRange, yMidpoint) => {
    const animations = [];

    // Get filtered index ranges from BOTH states
    // We need to check both because:
    // - For drag-select (new filter): State B has the filter data
    // - For reset (removing filter): State A has the filter data for the column being reset
    // A column is being reset if it's in State A's filter but not in State B's filter
    const indexRangesA = stateA ? stateA.getFilteredIndexRanges() : new Map();
    const indexRangesB = stateB ? stateB.getFilteredIndexRanges() : new Map();

    // Identify columns where nodes are appearing/disappearing (for label cross-fade)
    const filteredColumnsA = stateA ? stateA.getFilteredColumns() : new Set();
    const filteredColumnsB = stateB ? stateB.getFilteredColumns() : new Set();
    const hiddenColumnsA = stateA ? stateA.hiddenColumns : new Set();
    const hiddenColumnsB = stateB ? stateB.hiddenColumns : new Set();
    const columnsWithNodeChanges = new Set();

    // Column being filtered (added to filter set)
    filteredColumnsB.forEach(col => {
      if (!filteredColumnsA.has(col)) {
        columnsWithNodeChanges.add(col);
      }
    });

    // Column being reset (removed from filter set)
    filteredColumnsA.forEach(col => {
      if (!filteredColumnsB.has(col)) {
        columnsWithNodeChanges.add(col);
      }
    });

    // Column being shown (removed from hidden set)
    hiddenColumnsA.forEach(col => {
      if (!hiddenColumnsB.has(col)) {
        columnsWithNodeChanges.add(col);
      }
    });

    // Column being hidden (added to hidden set)
    hiddenColumnsB.forEach(col => {
      if (!hiddenColumnsA.has(col)) {
        columnsWithNodeChanges.add(col);
      }
    });

    // Check if this is a column hide/show operation (hiddenColumns changed)
    const isColumnHideShow = hiddenColumnsA.size !== hiddenColumnsB.size ||
      Array.from(hiddenColumnsA).some(col => !hiddenColumnsB.has(col)) ||
      Array.from(hiddenColumnsB).some(col => !hiddenColumnsA.has(col));

    // Note: We removed the old column-hide/show label width detection logic here.
    // Label width changes are now detected per-node in the animation loop below
    // using widthChangedForColumn, which is more precise.

    nodes.forEach(n => {
      if (n.id.startsWith('__anchor_')) return; // Skip anchor nodes
      if (!graph.hasNode(n.id)) return;

      // Calculate positions from state snapshots (immutable)
      const startX = stateA ? deriveX(n, stateA) : deriveX(n, stateB);
      const startY = stateA ? deriveY(n, stateA) : deriveY(n, stateB);
      const targetX = deriveX(n, stateB);
      const targetY = deriveY(n, stateB);

      const visibleInA = stateA ? stateA.isNodeVisible(n) : true;
      const visibleInB = stateB ? stateB.isNodeVisible(n) : true;

      // Get sizes from states
      const sizeInA = stateA ? calculateNodeSize(n.id, stateA) : DEFAULT_NODE_SIZE;
      const sizeInB = stateB ? calculateNodeSize(n.id, stateB) : DEFAULT_NODE_SIZE;

      // Check if node is in a column with appearing/disappearing nodes
      const columnHasNodeChanges = columnsWithNodeChanges.has(n.column);

      // Determine slide direction based on node position relative to filtered range
      // For appearing nodes (reset): use State A's range data (has filter info for column being reset)
      // For disappearing nodes (filter): use State B's range data (has filter info for column being filtered)
      // Try both states to find range data for this column
      const rangeDataA = indexRangesA.get(n.column);
      const rangeDataB = indexRangesB.get(n.column);
      // Use whichever state has filter data for this column
      // Reset: State A has it, State B doesn't. Filter: State B has it.
      const rangeData = rangeDataA || rangeDataB;
      const columnArray = columns[n.column]?.nodes || [];
      const nodeIndex = columnArray.findIndex(cn => cn.id === n.id);

      let animStartY, animTargetY;
      let animStartX, animTargetX;

      // Check if this is a drag-select/reset operation (has filter range) vs column hide/show
      const hasRangeData = rangeData && rangeData.filteredNodeIds;

      if (hasRangeData && nodeIndex !== -1) {
        // columnArray is now sorted by rank, so array indices match spatial order
        // Use array indices to determine slide direction

        // Find indices of selected nodes in the column array
        const selectedIndices = [];
        columnArray.forEach((node, idx) => {
          if (rangeData.filteredNodeIds.has(node.id)) {
            selectedIndices.push(idx);
          }
        });

        const rangeStart = Math.min(...selectedIndices);
        const rangeEnd = Math.max(...selectedIndices);

        // Find this node's index in the array
        const thisNodeIndex = columnArray.findIndex(node => node.id === n.id);

        // Simple rule: nodes before selection range slide UP, nodes after slide DOWN
        const slideUp = thisNodeIndex < rangeStart;

        // Sigma coordinates: low Y = top of screen, high Y = bottom of screen
        // To slide UP off top: use negative Y (e.g., -yRange * 0.2)
        // To slide DOWN off bottom: use high Y (e.g., yRange * 1.2)
        const offScreenY = slideUp ? -yRange * 0.2 : yRange * 1.2;

        if (!visibleInA && visibleInB) {
          // Appearing (reset): slide in from off-screen vertically (X stays at target)
          animStartX = targetX;
          animTargetX = targetX;
          animStartY = offScreenY;
          animTargetY = targetY;
        } else if (visibleInA && !visibleInB) {
          // Disappearing (filter): slide out to off-screen vertically (X stays at start)
          animStartX = startX;
          animTargetX = startX;
          animStartY = startY;
          animTargetY = offScreenY;
        }
      } else {
        // Column hide/show: collapse to or expand from center (both vertical and horizontal)
        // Calculate horizontal midpoint for State A (for appearing nodes)
        const visibleColumnsInA = stateA ? columns
          .map(col => col.index)
          .filter(colIndex => stateA.isColumnVisible(colIndex)) : [];

        let xMidpointA = startX;
        if (visibleColumnsInA.length >= 2) {
          const leftmostColA = Math.min(...visibleColumnsInA);
          const rightmostColA = Math.max(...visibleColumnsInA);
          const leftNodeA = { column: leftmostColA, visualColumn: 0, visibleColumnCount: visibleColumnsInA.length };
          const rightNodeA = { column: rightmostColA, visualColumn: visibleColumnsInA.length - 1, visibleColumnCount: visibleColumnsInA.length };
          const leftXA = deriveX(leftNodeA, stateA);
          const rightXA = deriveX(rightNodeA, stateA);
          xMidpointA = (leftXA + rightXA) / 2;
        }

        // Calculate horizontal midpoint for State B (for disappearing nodes)
        const visibleColumnsInB = columns
          .map(col => col.index)
          .filter(colIndex => stateB && stateB.isColumnVisible(colIndex));

        let xMidpointB = targetX;
        if (visibleColumnsInB.length >= 2) {
          const leftmostColB = Math.min(...visibleColumnsInB);
          const rightmostColB = Math.max(...visibleColumnsInB);
          const leftNodeB = { column: leftmostColB, visualColumn: 0, visibleColumnCount: visibleColumnsInB.length };
          const rightNodeB = { column: rightmostColB, visualColumn: visibleColumnsInB.length - 1, visibleColumnCount: visibleColumnsInB.length };
          const leftXB = deriveX(leftNodeB, stateB);
          const rightXB = deriveX(rightNodeB, stateB);
          xMidpointB = (leftXB + rightXB) / 2;
        }

        if (!visibleInA && visibleInB) {
          // Appearing: expand from State A center to final position in State B
          animStartX = xMidpointA;
          animTargetX = targetX;
          animStartY = yMidpoint;
          animTargetY = targetY;
        } else if (visibleInA && !visibleInB) {
          // Disappearing: collapse from State A position to State B center
          animStartX = startX;
          animTargetX = xMidpointB;
          animStartY = startY;
          animTargetY = yMidpoint;
        }
      }

      if (!visibleInA && visibleInB) {
        // Appearing: start offscreen/center with size 0, animate to final position/size
        graph.setNodeAttribute(n.id, "x", animStartX);
        graph.setNodeAttribute(n.id, "y", animStartY);
        animations.push({
          id: n.id,
          startX: animStartX,
          startY: animStartY,
          targetX: animTargetX,
          targetY: animTargetY,
          startSize: 0,
          targetSize: sizeInB,
          startOpacity: 0,
          targetOpacity: 1,
          startLabelOpacity: 0,
          targetLabelOpacity: 1,
          fadeInDuringAnimation: columnHasNodeChanges, // Columns with node changes fade in during animation
        });
      } else if (visibleInA && !visibleInB) {
        // Disappearing: animate to offscreen/center with size 0
        animations.push({
          id: n.id,
          startX: animStartX,
          startY: animStartY,
          targetX: animTargetX,
          targetY: animTargetY,
          startSize: sizeInA,
          targetSize: 0,
          startOpacity: 1,
          targetOpacity: 0,
          startLabelOpacity: 1,
          targetLabelOpacity: 0,
          fadeInDuringAnimation: false,
        });
      } else if (visibleInA && visibleInB) {
        // Moving/resizing: animate from State A position to State B position
        const xChanged = Math.abs(startX - targetX) > 0.1;
        const yChanged = Math.abs(startY - targetY) > 0.1;
        const sizeChanged = Math.abs(sizeInA - sizeInB) > 0.1;

        // Check if label is appearing (no label in A, has label in B)
        const hasLabelInA = stateA && stateA.nodeAggregates.has(n.id);
        const hasLabelInB = stateB && stateB.nodeAggregates.has(n.id);
        const labelAppearing = !hasLabelInA && hasLabelInB;

        if (xChanged || yChanged || sizeChanged || labelAppearing) {
          // Fade labels when:
          // 1. Column has nodes appearing/disappearing (cluster labeling ran)
          // 2. Label is appearing (no label in A, has label in B)
          // 3. Label width changed (different truncation length)
          const isLeftmost = n.column === 0;
          const isRightmost = n.column === columnCount - 1;
          const widthKey = isRightmost ? 'right' : isLeftmost ? 'left' : 'middle';
          const widthChangedForColumn = stateA && stateB &&
            stateA.columnLabelWidths[widthKey] !== stateB.columnLabelWidths[widthKey];

          const shouldFadeLabels = columnHasNodeChanges || labelAppearing || widthChangedForColumn;

          // Set initial label opacity based on whether label exists in State A
          const startLabelOpacity = labelAppearing ? 0 : (shouldFadeLabels ? 1 : 1);
          const targetLabelOpacity = hasLabelInB ? 1 : 0;

          animations.push({
            id: n.id,
            startX,
            startY,
            targetX,
            targetY,
            startSize: sizeInA,
            targetSize: sizeInB,
            startOpacity: 1,
            targetOpacity: 1,
            // Labels fade in if appearing, cross-fade if active column, stable otherwise
            startLabelOpacity,
            targetLabelOpacity,
            fadeInDuringAnimation: shouldFadeLabels, // Fade during animation for active columns or appearing labels
          });
        }
      }
    });

    return animations;
  }, [nodes, graph, columns, deriveX, deriveY, calculateNodeSize]);

  /**
   * Interpolate fade-out during first half of animation (0→0.5)
   * Returns 1 at start, 0 at midpoint, 0 after midpoint
   */
  const interpolateFadeOut = (progress) => {
    if (progress >= 0.5) return 0;
    const fadeProgress = progress * 2; // Map 0→0.5 to 0→1
    const eased = 1 - Math.pow(1 - fadeProgress, 3); // Cubic ease-out
    return 1 - eased; // Invert: start at 1, end at 0
  };

  /**
   * Interpolate fade-in during second half of animation (0.5→1.0)
   * Returns 0 before midpoint, 0 at midpoint, 1 at end
   */
  const interpolateFadeIn = (progress) => {
    if (progress <= 0.5) return 0;
    const fadeProgress = (progress - 0.5) * 2; // Map 0.5→1.0 to 0→1
    return 1 - Math.pow(1 - fadeProgress, 3); // Cubic ease-out
  };

  /**
   * Single-pass animation runner: interpolate all properties (position, size, opacity, labels)
   * Returns a cleanup function to cancel the animation
   */
  const runAnimation = useCallback((animations, stateA, stateB, onComplete) => {
    if (animations.length === 0) {
      onComplete();
      return () => {};
    }

    isAnimatingRef.current = true;
    setIsAnimating(true); // State for UI disabling
    onAnimationChange?.(true); // Notify parent that animation started
    const duration = ANIMATION_DURATION;
    const startTime = performance.now();
    let animationFrameId;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out

      // Create Maps for this frame's animated values
      const animatedNodeSizes = new Map();
      const animatedNodeOpacities = new Map();
      const animatedEdgeOpacities = new Map();
      const animatedLabelOpacities = new Map();

      // Interpolate all node properties
      animations.forEach(anim => {
        const {id, startX, startY, targetX, targetY,
               startSize, targetSize, startOpacity, targetOpacity,
               startLabelOpacity, targetLabelOpacity, fadeInDuringAnimation} = anim;

        // Position
        const newX = startX + (targetX - startX) * easeProgress;
        const newY = startY + (targetY - startY) * easeProgress;
        graph.setNodeAttribute(id, "x", newX);
        graph.setNodeAttribute(id, "y", newY);

        // Size
        const newSize = startSize + (targetSize - startSize) * easeProgress;
        animatedNodeSizes.set(id, newSize);

        // Node opacity - animate throughout FULL duration using easeProgress
        const newOpacity = startOpacity + (targetOpacity - startOpacity) * easeProgress;
        animatedNodeOpacities.set(id, newOpacity);

        // Label opacity - use proper interpolation functions with easing
        let newLabelOpacity;
        if (fadeInDuringAnimation && startLabelOpacity === targetLabelOpacity && startLabelOpacity === 1) {
          // Cross-fade pattern for moving nodes in active columns (1 → 0 → 1)
          // Fade out during first half, fade in during second half
          // This smoothly masks label text changes (e.g., short → long labels)
          if (progress < 0.5) {
            // First half: fade out (1 → 0)
            newLabelOpacity = interpolateFadeOut(progress);
          } else {
            // Second half: fade in (0 → 1)
            newLabelOpacity = interpolateFadeIn(progress);
          }
        } else if (startLabelOpacity < targetLabelOpacity) {
          // Fading in: use second-half fade-in
          newLabelOpacity = startLabelOpacity + (targetLabelOpacity - startLabelOpacity) * interpolateFadeIn(progress);
        } else if (startLabelOpacity > targetLabelOpacity) {
          // Fading out: use first-half fade-out
          newLabelOpacity = startLabelOpacity - (startLabelOpacity - targetLabelOpacity) * (1 - interpolateFadeOut(progress));
        } else {
          // No change in opacity (moving nodes NOT in active columns)
          newLabelOpacity = startLabelOpacity;
        }
        animatedLabelOpacities.set(id, newLabelOpacity);
      });

      // Fade edges connected to disappearing nodes (first half of animation)
      // Fade edges connected to appearing nodes (second half of animation)
      // Fade indirect edges based on column visibility changes
      if (stateA && stateB) {
        graph.forEachEdge((edgeId, attrs, source, target) => {
          const sourceNode = nodeByIdRef.current.get(source);
          const targetNode = nodeByIdRef.current.get(target);
          const sourceInA = stateA.isNodeVisible(sourceNode);
          const sourceInB = stateB.isNodeVisible(sourceNode);
          const targetInA = stateA.isNodeVisible(targetNode);
          const targetInB = stateB.isNodeVisible(targetNode);

          // Check if this is an indirect edge
          const isIndirect = attrs.isIndirect || false;

          if (isIndirect && sourceNode && targetNode) {
            // Indirect edges: fade based on BOTH column visibility AND node visibility
            // An indirect edge is visible when:
            // 1. All intermediate columns are hidden (column-level)
            // 2. Both endpoints are visible (node-level)
            const sourceCol = sourceNode.column;
            const targetCol = targetNode.column;
            const minCol = Math.min(sourceCol, targetCol);
            const maxCol = Math.max(sourceCol, targetCol);

            // Check if edge should be visible in State A (column-level)
            let columnVisibleInA = true;
            for (let col = minCol + 1; col < maxCol; col++) {
              if (stateA.isColumnVisible(col)) {
                columnVisibleInA = false;
                break;
              }
            }

            // Check if edge should be visible in State B (column-level)
            let columnVisibleInB = true;
            for (let col = minCol + 1; col < maxCol; col++) {
              if (stateB.isColumnVisible(col)) {
                columnVisibleInB = false;
                break;
              }
            }

            // Indirect edge is fully visible when columns are hidden AND nodes are visible
            const fullyVisibleInA = columnVisibleInA && sourceInA && targetInA;
            const fullyVisibleInB = columnVisibleInB && sourceInB && targetInB;

            if (fullyVisibleInA !== fullyVisibleInB) {
              // Indirect edge visibility is changing
              if (!fullyVisibleInA && fullyVisibleInB) {
                // Edge appearing: fade in during second half
                animatedEdgeOpacities.set(edgeId, interpolateFadeIn(progress));
              } else if (fullyVisibleInA && !fullyVisibleInB) {
                // Edge disappearing: fade out during first half
                animatedEdgeOpacities.set(edgeId, interpolateFadeOut(progress));
              }
            }
          } else if (!sourceInA || !targetInA || !sourceInB || !targetInB) {
            // Direct edges connected to appearing/disappearing nodes
            const disappearing = (sourceInA && !sourceInB) || (targetInA && !targetInB);
            if (disappearing) {
              // Fade out during first half
              animatedEdgeOpacities.set(edgeId, interpolateFadeOut(progress));
            } else {
              // Fade in during second half
              animatedEdgeOpacities.set(edgeId, interpolateFadeIn(progress));
            }
          }
        });
      }

      // Update ViewState with this frame's values
      viewStateRef.current = new ViewState({
        ...viewStateRef.current,
        animatedNodeSizes,
        animatedNodeOpacities,
        animatedEdgeOpacities,
        animatedLabelOpacities,
        animationProgress: progress,  // Track animation progress (0-1) for cross-fade timing
      });

      if (progress < 1) {
        sigma.refresh();
        animationFrameId = requestAnimationFrame(animate);
      } else {
        // Animation complete - clear animation state BEFORE updating state
        // This prevents a flash where nodes render with new state but stale animated values
        isAnimatingRef.current = false;
        setIsAnimating(false);
        onAnimationChange?.(false); // Notify parent that animation ended

        // Clear animated values BEFORE calling onComplete
        // This ensures the final render uses stable state without animation artifacts
        viewStateRef.current = new ViewState({
          ...viewStateRef.current,
          previousGraphState: null,
          targetGraphState: null,
          animatedNodeSizes: new Map(),
          animatedNodeOpacities: new Map(),
          animatedEdgeOpacities: new Map(),
          animatedLabelOpacities: new Map(),
        });

        // Now update state and render final frame
        onComplete();
      }
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [graph, sigma]);

  // Main animation loop: runs when targetState is non-null
  // Animates from currentState → targetState
  // Upon completion: currentState = targetState, targetState = null
  // Enforces no concurrent animations (only runs when targetState changes)
  useEffect(() => {
    // No-op if targetState is null
    if (!targetState) {
      return;
    }

    // Enforce preconditions
    if (!currentState) {
      setTargetState(null);
      return;
    }

    if (!sigma || !graph || graph.order === 0) {
      return;
    }

    // Animate from currentState → targetState
    const stateA = currentState;
    const stateB = targetState;

    // Build animations from State A → State B
    const yRange = yMultiplier(width, height);
    const yMidpoint = yRange / 2;
    const animations = buildAnimations(stateA, stateB, yRange, yMidpoint);

    if (animations.length === 0) {
      // No animation needed - states are identical, just clear targetState
      setTargetState(null);
      sigma.refresh();
      return;
    }

    // Set ViewState snapshots for nodeReducer/edgeReducer to access during animation
    viewStateRef.current = new ViewState({
      ...viewStateRef.current,
      previousGraphState: stateA,
      targetGraphState: stateB,
    });

    const cleanup = runAnimation(animations, stateA, stateB, () => {
      // Animation complete: currentState = targetState, targetState = null
      setCurrentState(stateB);
      setTargetState(null);

      // CRITICAL: Clear preserved aggregates after animation completes
      // This forces computeGraphState to re-calculate clusters based on the new filtered nodes
      // Otherwise, nodes remain treated as members of the pre-filter expanded cluster
      state.clickPreservedAggregates = null;

      // ViewState was already cleared in runAnimation before calling onComplete
      // Defer refresh until after setState takes effect (next frame)
      // This prevents rendering with stale state
      requestAnimationFrame(() => {
        sigma.refresh();
      });
    });

    return cleanup;
  }, [targetState, currentState, sigma, graph, width, height, yMultiplier]);

  const onSelectionEnd = useCallback(({nodes: dragSelectedNodeIDs, canceled = false}) => {
    if (isAnimatingRef.current) return;
    if (canceled) {
      // TODO: Restore original selection
      dragSelectedNodeIDs = null;
    }
    if (dragSelectedNodeIDs && dragSelectedNodeIDs.length) {
      // Drag-select provides spatially selected node IDs - use them directly
      // Clusters are only for display, not for selection
      const expandedNodeIDs = new Set(dragSelectedNodeIDs);

      // Build set of nodes from current drag-select operation
      const dragSelectedNodes = nodes.filter(n => expandedNodeIDs.has(n.id));

      const dragSelectedColumns = dragSelectedNodes.reduce((result, node) => {
        result.add(node.column);
        return result;
      }, new Set());

      if (!dragSelectedColumns.size) {
        return;
      }

      console.log('[DragSelect] Filtering columns:', Array.from(dragSelectedColumns));

      // Calculate index ranges for drag-selected nodes
      const newFilteredIndexRanges = new Map(filteredIndexRanges);

      Array.from(dragSelectedColumns).forEach(col => {
        const columnNodes = dragSelectedNodes.filter(n => n.column === col);

        console.log('[DragSelect] Selected', columnNodes.length, 'nodes in column', col);

        // Store the filtered node IDs directly (from drag-select operation)
        const filteredNodeIds = new Set(columnNodes.map(n => n.id));
        newFilteredIndexRanges.set(col, { filteredNodeIds });


        // Normalize score range for similarity sorting (if needed)
        if (columnNodes.every(n => n.score !== undefined)) {
          const minScore = Math.min(1, ...columnNodes.map(n => n.score));
          const maxScore = Math.max(0, ...columnNodes.map(n => n.score));
          const d= maxScore - minScore;
          columnNodes.forEach((n, idx) => {
            n.scaledScore = d ? (n.score - minScore) / d : idx / columnNodes.length;
          });
        }
      });

      // Update filter ranges (source of truth) - will trigger state recomputation
      // Filtered nodes will be derived from ranges in state initialization effect
      setFilteredIndexRanges(newFilteredIndexRanges);

      // Hide labels during position animation
      labelOpacityRef.current = 0;
    }
    state.selectedNodeIDs = state.selectedEdges = state.hoverNodes = state.hoverEdges = undefined;
    state.hoveredColumn = undefined; // Clear hovered column
  }, [sigma, nodes, filteredIndexRanges, columns]);

  const selectionState = useSelectionRectangle(sigma, container, {
    onSelectionChange,
    onSelectionEnd,
    disabled: isAnimatingRef,
  });

  useEffect(() => {
    state.selectedEdges = state.selectedNodeIDs = state.hoverEdges = state.hoverNodes = undefined;
    state.hoveredColumn = undefined; // Clear hovered column

    // Restore label filter hover state if active (prevents loss on window blur/focus)
    if (labelFilter !== "") {
      const matched = graph.nodes().filter(id => {
        return graph.getNodeAttribute(id, "label")?.toLowerCase().indexOf(labelFilter.toLowerCase()) !== -1;
      });
      state.hoverNodes = new Set(matched);
    }

    sigma.refresh();

    // CAMERA POSITIONING - DISABLED
    // The reset camera button uses normalized coordinates (0.5, 0.5, ratio=1),
    // but graph coordinates are in a different space (0-1000+).
    // Let Sigma auto-fit the graph for now until we understand the coordinate system.
    //
    // TODO: Implement proper camera positioning with correct coordinate transforms
    // to account for margins (20px all sides, 170px label width, 60px legend height)

    // Prevent all camera movements (zoom AND pan) at the JavaScript level
    // This blocks trackpad pinch/scroll/drag gestures that bypass CSS touch-action
    const camera = sigma.getCamera();
    const lockedCameraState = camera.getState();

    const onCameraUpdate = () => {
      const newState = camera.getState();
      // If ANY camera property changed (x, y, ratio, angle), revert it
      if (newState.x !== lockedCameraState.x
          || newState.y !== lockedCameraState.y
          || newState.ratio !== lockedCameraState.ratio
          || newState.angle !== lockedCameraState.angle) {
        camera.setState(lockedCameraState);
      }
    };
    camera.on('updated', onCameraUpdate);

    return () => {
      camera.off('updated', onCameraUpdate);
    };
  }, [nodes, adjacency, sigma, width, height, container, labelFilter, graph]);

  useEffect(() => {
    const columnVisibilityChanged =
      prevColumnVisibilityRef.current.showSimilarity !== null &&
      (prevColumnVisibilityRef.current.showSimilarity !== showSimilarity ||
       prevColumnVisibilityRef.current.showIntermediate !== showIntermediate);

    // Trigger animation when column visibility changes (not on first render)
    if (columnVisibilityChanged && graph.order > 0) {
      // Detect which column is being toggled
      const prevSimilarity = prevColumnVisibilityRef.current.showSimilarity;
      const prevIntermediate = prevColumnVisibilityRef.current.showIntermediate;

      let collapsingColumnIndex = -1;
      let expandingColumnIndex = -1;

      if (prevSimilarity !== showSimilarity) {
        collapsingColumnIndex = !showSimilarity ? similarityColumn : -1;
        expandingColumnIndex = showSimilarity ? similarityColumn : -1;
      }
      if (prevIntermediate !== showIntermediate) {
        collapsingColumnIndex = !showIntermediate ? intermediateColumn : -1;
        expandingColumnIndex = showIntermediate ? intermediateColumn : -1;
      }

      // Note: hiddenColumns is now part of GraphState (currentState.hiddenColumns)
      // Animation tracking is derived from ViewState.previousGraphState/targetGraphState
      // The animation useEffect will set up the GraphState snapshots

      // Update state (now safe - ViewState.isColumnHidden() already set)
      state.showSimilarity = showSimilarity;
      state.showIntermediate = showIntermediate;
      state.similarityColumn = similarityColumn;
      state.intermediateColumn = intermediateColumn;

      // CRITICAL: deriveX now ALWAYS uses GraphState to calculate positions
      // This ensures animation works by comparing State A vs State B:
      //
      // State A (before hide): graphState.hiddenColumns = {}
      //   - visibleColumns = [0, 1, 2]
      //   - RHS node (column=2) gets visualIndex=2, position = 1.0 * multiplier
      //
      // State B (after hide): graphState.hiddenColumns = {1}
      //   - visibleColumns = [0, 2]
      //   - RHS node (column=2) gets visualIndex=1, position = 0.72 * multiplier
      //
      // Animation: RHS slides left from 1.0 to 0.72
      //
      // No need to mutate node properties - GraphState provides all position info

      // Update ref for next render
      prevColumnVisibilityRef.current = { showSimilarity, showIntermediate };
    } else if (prevColumnVisibilityRef.current.showSimilarity === null) {
      // First render - save initial values
      prevColumnVisibilityRef.current = { showSimilarity, showIntermediate };

      // On first render, refresh immediately since there's no animation
      sigma.refresh();
    } else {
      // State changed but no animation needed (e.g., columnCount changed)
      sigma.refresh();
    }
  }, [showSimilarity, showIntermediate, similarityColumn, intermediateColumn, columnCount]);

  // columnHidden callback removed - use viewStateRef.current.isColumnHidden() instead
  // This allows proper animation handling since ViewState returns false during animation

  const clearFilter = useCallback((column = null) => {
    // NOTE: this handler will not be called if there was a drag
    // https://github.com/jacomyal/sigma.js/blob/89a1be9953dd3488546fabc880dac262d5019c67/packages/sigma/src/core/captors/mouse.ts#L127
    if (column === null) {
      // Clear selection state when clicking stage
      state.selectedNodeIDs = state.selectedEdges = undefined;
      state.primarySelectedNode = undefined;

      // Check if any filters are active (derive from currentState)
      const filteredColumns = currentState?.getFilteredColumns() || new Set();
      if (filteredColumns.size > 0) {
        nodes.forEach(n => {
          delete n.scaledScore;
        });

        // Reset positions of any dragged nodes back to their computed layout positions
        // This ensures that when filters are cleared, nodes return to their default positions
        nodes.forEach(n => {
          if (graph.hasNode(n.id) && currentState) {
            const targetX = deriveX(n, currentState);
            const targetY = deriveY(n, currentState);
            graph.setNodeAttribute(n.id, "x", targetX);
            graph.setNodeAttribute(n.id, "y", targetY);
          }
        });

        // Clear click-preserved aggregates when resetting all filters
        // This prevents stale hover-time clustering from persisting after filter reset
        state.clickPreservedAggregates = null;

        // Clear filter ranges (source of truth) - they're preserved in State A for animation
        // Filtered nodes will be automatically derived (all nodes) in next state computation
        setFilteredIndexRanges(new Map());

        // Hide labels during animation, fade in after
        labelOpacityRef.current = 0;
      }
    }
    else {
      // Restore nodes for one column only
      // Check if this column has a filter (entry exists in currentState.visibleNodesPerColumn)
      const filteredColumns = currentState?.getFilteredColumns() || new Set();
      if (filteredColumns.has(column)) {
        // Clear click-preserved aggregates when resetting a column filter
        // This prevents stale hover-time clustering from persisting after filter reset
        state.clickPreservedAggregates = null;

        // Clear this column's range - it's preserved in State A for animation
        // Filtered nodes will be automatically derived in next state computation
        const newRanges = new Map(filteredIndexRanges);
        newRanges.delete(column);
        setFilteredIndexRanges(newRanges);

        nodes
          .filter(n => n.column === column)
          .forEach(n => {
            delete n.scaledScore;

            // Reset positions of any dragged nodes back to their computed layout positions
            // This ensures that when a column filter is cleared, nodes return to their default positions
            if (graph.hasNode(n.id) && currentState) {
              const targetX = deriveX(n, currentState);
              const targetY = deriveY(n, currentState);
              graph.setNodeAttribute(n.id, "x", targetX);
              graph.setNodeAttribute(n.id, "y", targetY);
            }
          });

        // Hide labels during animation
        labelOpacityRef.current = 0;
      }
    }
  }, [nodes, filteredIndexRanges, currentState]);

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "Escape") {
        clearFilter();
      }
    };
    const body = window.document.body;
    body.addEventListener("keydown", onKeyDown);
    return () => {
      body.removeEventListener("keydown", onKeyDown);
    };
  }, [clearFilter]);

  const expandFromNode = (key, expandRight) => {
    const column = graph.getNodeAttribute(key, "column");
    const result = Array.from(graph.reduceNeighbors(key, (nodes, nbrKey, nbrIdx) => {
      const nbrColumn = graph.getNodeAttribute(nbrKey, "column");
      const columnVisible = currentState ? currentState.isColumnVisible(nbrColumn) : false;
      const directionMatch = expandRight ? nbrColumn > column : nbrColumn < column;

      if (currentState && currentState.isColumnVisible(nbrColumn)) {
        if (expandRight ? nbrColumn > column : nbrColumn < column) {
          nodes.add(nbrKey);
          if ((expandRight && nbrColumn < columnCount - 1) || (!expandRight && nbrColumn > 0)) {
            expandFromNode(nbrKey, expandRight).forEach(nn => nodes.add(nn));
          }
        }
      }
      return nodes;
    }, new Set()));

    return result;
  };

  const isolateChains = (node) => {
    const leftNodes = [node, ...expandFromNode(node, false)];
    const left = subgraph(graph, leftNodes);
    const rightNodes = [node, ...expandFromNode(node, true)];
    const right = subgraph(graph, rightNodes);
    return union(left, right);
  };

  const handleNodeEnter = useCallback(({node}) => {
    if (isAnimatingRef.current || selectionState.isActive) {
      return;
    }

    // When labelFilter is active, check if node matches the filter
    let filterActiveNodes = null;
    if (labelFilter) {
      // Compute the filter set fresh - don't rely on state.hoverNodes which may have been modified
      const matched = graph.nodes().filter(id => {
        return graph.getNodeAttribute(id, "label")?.toLowerCase().indexOf(labelFilter.toLowerCase()) !== -1;
      });
      filterActiveNodes = new Set(matched);

      // Only allow hover on matching nodes
      if (!filterActiveNodes.has(node)) {
        return;
      }
    }

    // HOVER PRIORITY: When hovering a non-selected node, check if any selected nodes are at the same position.
    // This handles the case where nodes are members of BOTH a selected cluster and a non-selected cluster.
    // The same physical node can have different rendering states (z-index, color) depending on which cluster
    // context it's being rendered in. Selected nodes should always take priority.
    const isHoveredSelected = state.selectedNodeIDs?.has(node);

    if (!isHoveredSelected && state.selectedNodeIDs && state.selectedNodeIDs.size > 0) {
      // This is a non-selected node being hovered while there are selected nodes
      // Check if any selected nodes are at the same position (spatially coincident)
      const nodeX = graph.getNodeAttribute(node, "x");
      const nodeY = graph.getNodeAttribute(node, "y");
      const nodeSize = graph.getNodeAttribute(node, "size") || DEFAULT_NODE_SIZE;

      // Check all selected nodes for spatial coincidence
      for (const selectedId of state.selectedNodeIDs) {
        if (!graph.hasNode(selectedId)) continue;
        const selX = graph.getNodeAttribute(selectedId, "x");
        const selY = graph.getNodeAttribute(selectedId, "y");
        const selSize = graph.getNodeAttribute(selectedId, "size") || DEFAULT_NODE_SIZE;

        const distance = Math.sqrt((nodeX - selX)**2 + (nodeY - selY)**2);

        // If a selected node is at the same position (within visual overlap), redirect to it
        // Use the larger of the two node sizes as the threshold
        const overlapThreshold = Math.max(nodeSize, selSize);

        if (distance <= overlapThreshold) {
          // Selected node is spatially coincident - redirect hover to it instead
          handleNodeEnter({node: selectedId});
          return; // Abort processing the non-selected node
        }
      }
    }

    // Find the cluster containing this node (if any)
    const nodeColumn = graph.getNodeAttribute(node, "column");
    let clusterNodeIds = new Set([node]);
    let clusterRepresentative = node; // Default to hovered node

    // CRITICAL: When hovering a selected node, use the selected cluster aggregates
    // This prevents using the wrong cluster size (e.g., 17-node static cluster
    // instead of 9-node selected cluster) when the same node is representative
    // for both clusters
    const isSelected = state.selectedNodeIDs?.has(node);
    const aggregatesToUse = isSelected && state.selectedAggregates
      ? state.selectedAggregates
      : (currentState ? currentState.nodeAggregates : new Map());

    if (aggregatesToUse.size > 0) {
      // Check if this node is a representative in the selected/static aggregates
      if (aggregatesToUse.has(node)) {
        const aggregate = aggregatesToUse.get(node);
        clusterNodeIds = new Set(aggregate.nodeIds);
        clusterRepresentative = node;
      } else {
        // Check if any cluster contains this node (as a non-representative)
        for (const [repId, agg] of aggregatesToUse.entries()) {
          if (agg.nodeIds.includes(node)) {
            clusterNodeIds = new Set(agg.nodeIds);
            clusterRepresentative = repId; // This is the representative
            break;
          }
        }
      }

      // When labelFilter is active, filter cluster to only include matching nodes
      if (filterActiveNodes) {
        const filteredClusterIds = new Set();
        for (const nodeId of clusterNodeIds) {
          if (filterActiveNodes.has(nodeId)) {
            filteredClusterIds.add(nodeId);
          }
        }
        clusterNodeIds = filteredClusterIds;
        // If the original representative was filtered out, use the hovered node as representative
        if (!filterActiveNodes.has(clusterRepresentative)) {
          clusterRepresentative = node;
        }
      }
    }

    // For each node in the cluster, find its connected subgraph
    const allHoverNodes = new Set();
    const allHoverEdges = new Set();

    // First, add all cluster members (including representative) to hoverNodes
    console.log(`[HandleNodeEnter] Hovered node: ${node.substring(0, 30)}, column: ${nodeColumn}`);
    console.log(`[HandleNodeEnter] Cluster representative: ${clusterRepresentative.substring(0, 30)}, cluster size: ${clusterNodeIds.size}`);
    console.log(`[HandleNodeEnter] Cluster members:`, Array.from(clusterNodeIds).map(id => id.substring(0, 30)));
    clusterNodeIds.forEach(id => allHoverNodes.add(id));

    // Then add their connected subgraphs
    for (const clusterNodeId of clusterNodeIds) {
      const g = isolateChains(clusterNodeId);
      g.nodes().forEach(n => allHoverNodes.add(n));
      // Only add direct edges to hover set (exclude indirect/synthetic edges)
      g.edges().forEach(e => {
        const edgeData = graph.getEdgeAttributes(e);
        if (!edgeData.isIndirect) {
          allHoverEdges.add(e);
        }
      });
    }

    // Compute which hover nodes should show labels based on vertical spacing
    // Group hover nodes by column and run cluster labeling on each column
    const hoverLabelNodes = new Set();
    const nodesByColumn = new Map();

    allHoverNodes.forEach(nodeId => {
      if (!graph.hasNode(nodeId)) return;
      // When labelFilter is active, only aggregate over matching nodes
      if (filterActiveNodes && !filterActiveNodes.has(nodeId)) return;

      const col = graph.getNodeAttribute(nodeId, "column");
      if (!nodesByColumn.has(col)) {
        nodesByColumn.set(col, []);
      }
      const y = graph.getNodeAttribute(nodeId, "y");
      const nodeData = nodeByIdRef.current.get(nodeId);
      const rank = nodeData?.rank || 0;
      nodesByColumn.get(col).push({ id: nodeId, y, rank });
    });

    // For each column, compute which nodes should show labels
    const hoverAggregates = new Map(); // Track hover-time cluster aggregation
    nodesByColumn.forEach((nodesInCol, col) => {
      console.log(`[HoverLabel] Column ${col}: processing ${nodesInCol.length} hover nodes`);

      // Sort by viewport pixel Y
      nodesInCol.forEach(n => {
        const viewportPos = sigma.graphToViewport({ x: 0, y: n.y });
        n.pixelY = viewportPos.y;
      });
      nodesInCol.sort((a, b) => a.pixelY - b.pixelY);

      // Apply cluster labeling algorithm to hover nodes in this column
      // This includes the hovered column - labels need to be regenerated for reduced node density
      console.log(`[HoverLabel] Column ${col} nodes before clustering:`, nodesInCol.map(n => `${n.id.substring(0, 20)} pixelY=${n.pixelY.toFixed(1)}`));
      const aggregation = applyClusterLabeling(nodesInCol, labelSpacing, sigma, graph, nodeByIdRef, col);
      console.log(`[HoverLabel] Column ${col} aggregation result:`, Array.from(aggregation.keys()).map(k => k.substring(0, 20)));

      // Add all labeled nodes to the hover label set
      aggregation.forEach((clusterInfo, nodeId) => {
        hoverLabelNodes.add(nodeId);
        hoverAggregates.set(nodeId, clusterInfo); // Store hover aggregation for nodeReducer
      });

      // For the hovered column: ALWAYS ensure the cluster representative is in hoverLabelNodes
      // This ensures the cluster description shows when ANY cluster member is hovered
      if (col === nodeColumn && clusterRepresentative) {
        hoverLabelNodes.add(clusterRepresentative);
        console.log(`[HoverLabel] Ensured cluster representative ${clusterRepresentative.substring(0, 30)} is in hoverLabelNodes for hovered column ${col}`);
      }
    });

    state.hoverNodes = allHoverNodes;
    state.hoverEdges = allHoverEdges;
    state.hoverLabelNodes = hoverLabelNodes; // Track which hover nodes should show labels
    state.hoverAggregates = hoverAggregates; // Track hover-time cluster aggregation
    state.primaryHoverNode = node; // Track the specific node being hovered
    state.clusterRepresentative = clusterRepresentative; // Track the cluster representative node
    state.hoveredColumn = nodeColumn; // Track the column for label hiding

    // Call onDetailTargetChange for single node hover to display EntityView in RHS panel
    if (onDetailTargetChange && clusterNodeIds.size === 1) {
      // Single node hover - pass node ID to show EntityView
      onDetailTargetChange(node, null, {isSingleNode: true});
    }

    updateEdges();
    sigma.refresh()
  }, [sigma, graph, currentState, labelSpacing, labelFilter]);

  useEffect(() => {
    if (selectionState.isActive) {
      return;
    }
    if (labelFilter !== "") {
      const matched = graph.nodes().filter(id => {
        return graph.getNodeAttribute(id, "label")?.toLowerCase().indexOf(labelFilter.toLowerCase()) !== -1;
      });
      state.hoverNodes = new Set(matched);
    }
    else {
      state.hoverNodes = undefined;
    }
    state.hoverEdges = undefined;
    sigma.refresh();
  }, [labelFilter]);

  const handleNodeLeave = useCallback(({node}) => {
    if (isAnimatingRef.current || selectionState.isActive) {
      return;
    }

    // When labelFilter is active, restore the filter hover state after leaving a node
    if (labelFilter) {
      // Clear the interactive hover state but restore filter matches
      state.primaryHoverNode = undefined;
      state.hoveredColumn = undefined;
      state.hoverEdges = undefined;
      state.hoverAggregates = undefined;
      state.clusterRepresentative = undefined;
      const matched = graph.nodes().filter(id => {
        return graph.getNodeAttribute(id, "label")?.toLowerCase().indexOf(labelFilter.toLowerCase()) !== -1;
      });
      state.hoverNodes = new Set(matched);
      updateEdges();
      sigma.refresh();
      return;
    }

    state.hoverNodes = state.hoverEdges = undefined;
    state.primaryHoverNode = undefined; // Clear primary hover
    state.hoveredColumn = undefined; // Clear hovered column
    updateEdges();
    sigma.refresh();
  }, [sigma, graph, labelFilter]);

  const updateEdges = () => {
    if (state.hoverEdges?.size === 1) {
      handleEdgeSelect(Array.from(state.hoverEdges)[0]);
    }
    else if (state.hoverEdges) {
      handleEdgeSelect();
    }
    else if (state.selectedEdges?.size === 1) {
      handleEdgeSelect(Array.from(state.selectedEdges)[0]);
    }
    else {
      handleEdgeSelect();
    }
  }

  const handleNodeClick = useCallback(({node}) => {
    if (isAnimatingRef.current) return;

    // Find the cluster containing the clicked node
    const nodeData = nodeByIdRef.current.get(node);
    if (!nodeData || !currentState) {
      return;
    }

    Analytics.event(Analytics.EVENTS.GRAPH_NODE_CLICK, {
      node_id: node,
      node_label: nodeData.label || node,
    });

    const nodeColumn = nodeData.column;
    let clusterNodeIds = [node];

    // Check if this node is part of a cluster
    const aggregate = currentState.getAggregate(node);
    if (aggregate.count > 1) {
      // This node IS the representative - include all cluster nodes
      clusterNodeIds = aggregate.nodeIds;
    } else {
      // Check if any cluster contains this node (as a non-representative)
      for (const [repId, agg] of currentState.nodeAggregates.entries()) {
        if (agg.nodeIds.includes(node)) {
          clusterNodeIds = agg.nodeIds;
          break;
        }
      }
    }

    console.log('[NodeClick] Clicked node:', node.substring(0, 30), 'column:', nodeColumn, 'cluster size:', clusterNodeIds.length);
    console.log('[NodeClick] Hover state exists:', state.hoverNodes && state.hoverNodes.size > 0);
    console.log('[NodeClick] Hover aggregates size:', state.hoverAggregates ? state.hoverAggregates.size : 0);

    // Check if this is a cluster node (count > 1)
    if (clusterNodeIds.length > 1) {
      // CLUSTER NODE: Apply filtering behavior (filter all connected nodes in all columns)
      console.log('[NodeClick] Cluster node - applying filtering behavior');

      // CRITICAL: Preserve hover-time aggregates before clearing hover state
      // This ensures the clustering visible during hover persists after clicking
      const preservedAggregates = state.hoverAggregates ? new Map(state.hoverAggregates) : null;
      console.log('[NodeClick] Preserving', preservedAggregates?.size || 0, 'hover-time aggregates');
      if (preservedAggregates) {
        console.log('[NodeClick] Preserved aggregate details:');
        for (const [nodeId, agg] of preservedAggregates.entries()) {
          const nodeData = nodeByIdRef.current.get(nodeId);
          console.log(`  - ${nodeId.substring(0, 30)} (col=${nodeData?.column}): count=${agg.count}, nodeIds=${agg.nodeIds.length}`);
        }
      }

      // Find ALL connected nodes across all columns (same as hover highlighting)
      // For each node in the cluster, find its connected subgraph
      const allConnectedNodeIds = new Set();
      clusterNodeIds.forEach(clusterNodeId => {
        const g = isolateChains(clusterNodeId);
        g.nodes().forEach(n => allConnectedNodeIds.add(n));
      });

      console.log('[NodeClick] Total connected nodes across all columns:', allConnectedNodeIds.size);

      // Group connected nodes by column
      const connectedNodesByColumn = new Map();
      allConnectedNodeIds.forEach(nodeId => {
        const n = nodeByIdRef.current.get(nodeId);
        if (!n) return;

        if (!connectedNodesByColumn.has(n.column)) {
          connectedNodesByColumn.set(n.column, []);
        }
        connectedNodesByColumn.get(n.column).push(n);
      });

      // Create filters for each column with connected nodes
      const newFilteredIndexRanges = new Map(filteredIndexRanges);

      connectedNodesByColumn.forEach((connectedNodes, col) => {
        console.log('[NodeClick] Column', col, ':', connectedNodes.length, 'connected nodes');

        // Store the filtered node IDs directly
        const filteredNodeIds = new Set(connectedNodes.map(n => n.id));
        newFilteredIndexRanges.set(col, { filteredNodeIds });

        // Normalize score range for similarity sorting (if needed)
        if (connectedNodes.every(n => n.score !== undefined)) {
          const minScore = Math.min(1, ...connectedNodes.map(n => n.score));
          const maxScore = Math.max(0, ...connectedNodes.map(n => n.score));
          const d = maxScore - minScore;
          connectedNodes.forEach((n, idx) => {
            n.scaledScore = d ? (n.score - minScore) / d : idx / connectedNodes.length;
          });
        }
      });

      // DO NOT preserve hover-time aggregates for cluster click filtering
      // State B (animation target) should compute fresh cluster/label layout
      // based on the new filtered node set, not preserve old aggregates
      // This ensures labels update correctly after animation completes
      state.clickPreservedAggregates = null;

      // Update filter ranges (source of truth) - will trigger state recomputation
      // computeGraphState will calculate new clusters based on filtered nodes
      setFilteredIndexRanges(newFilteredIndexRanges);

      // Hide labels during position animation
      labelOpacityRef.current = 0;

      // Clear interaction state (same as onSelectionEnd)
      state.selectedNodeIDs = state.selectedEdges = state.hoverNodes = state.hoverEdges = undefined;
      state.hoveredColumn = undefined;

      // Restore label filter state if active (prevent cluster click from clearing filter)
      if (labelFilter !== "") {
        const matched = graph.nodes().filter(id => {
          return graph.getNodeAttribute(id, "label")?.toLowerCase().indexOf(labelFilter.toLowerCase()) !== -1;
        });
        state.hoverNodes = new Set(matched);
      }
    } else {
      // SINGLE NODE: Clone hover state to selection state (makes it persistent)
      console.log('[NodeClick] Single node - cloning hover state to selection');

      // If hover state exists, clone it to selection (captures exact same aggregates/labels)
      // Otherwise fall back to basic isolateChains behavior
      if (state.hoverNodes && state.hoverNodes.size > 0) {
        state.selectedNodeIDs = new Set(state.hoverNodes);
        state.selectedEdges = new Set(state.hoverEdges);
        state.selectedLabelNodes = state.hoverLabelNodes ? new Set(state.hoverLabelNodes) : undefined;
        state.selectedAggregates = state.hoverAggregates ? new Map(state.hoverAggregates) : undefined;
        state.clusterRepresentative = state.clusterRepresentative; // Keep cluster representative from hover

        console.log('[NodeClick] Cloned hover aggregates:', state.selectedAggregates?.size || 0);
      } else {
        // No hover state - re-run cluster labeling for selected nodes
        console.log('[NodeClick] No hover state - computing fresh aggregates from isolateChains');
        const g = isolateChains(node);
        state.selectedNodeIDs = new Set(g.nodes());

        // Only include direct edges in selection (exclude indirect/synthetic edges)
        const directEdges = new Set();
        g.edges().forEach(e => {
          const edgeData = graph.getEdgeAttributes(e);
          if (!edgeData.isIndirect) {
            directEdges.add(e);
          }
        });
        state.selectedEdges = directEdges;

        // Re-run cluster labeling for the selected connected nodes
        // Group selected nodes by column and run applyClusterLabeling on each
        const selectedAggregates = new Map();
        const selectedLabelNodes = new Set();
        const nodesByColumn = new Map();

        g.nodes().forEach(nodeId => {
          if (!graph.hasNode(nodeId)) return;
          const col = graph.getNodeAttribute(nodeId, "column");
          if (!nodesByColumn.has(col)) {
            nodesByColumn.set(col, []);
          }
          const y = graph.getNodeAttribute(nodeId, "y");
          const nodeData = nodeByIdRef.current.get(nodeId);
          const rank = nodeData?.rank || 0;
          nodesByColumn.get(col).push({ id: nodeId, y, rank });
        });

        // For each column, run cluster labeling
        nodesByColumn.forEach((nodesInCol, col) => {
          // Sort by viewport pixel Y
          nodesInCol.forEach(n => {
            const viewportPos = sigma.graphToViewport({ x: 0, y: n.y });
            n.pixelY = viewportPos.y;
          });
          nodesInCol.sort((a, b) => a.pixelY - b.pixelY);

          // Apply cluster labeling
          const aggregation = applyClusterLabeling(nodesInCol, labelSpacing, sigma, graph, nodeByIdRef, col);

          // Store aggregates and mark labeled nodes
          aggregation.forEach((clusterInfo, nodeId) => {
            selectedLabelNodes.add(nodeId);
            selectedAggregates.set(nodeId, clusterInfo);
          });
        });

        state.selectedLabelNodes = selectedLabelNodes;
        state.selectedAggregates = selectedAggregates;
        console.log('[NodeClick] Computed fresh aggregates:', state.selectedAggregates?.size || 0);
      }

      state.primarySelectedNode = node;

      // Clear hover state since it's now selected
      state.hoverNodes = state.hoverEdges = state.hoverLabelNodes = state.hoverAggregates = undefined;
      state.primaryHoverNode = undefined;
      state.hoveredColumn = undefined;

      // Restore label filter state if active (prevent single node click from clearing filter)
      if (labelFilter !== "") {
        const matched = graph.nodes().filter(id => {
          return graph.getNodeAttribute(id, "label")?.toLowerCase().indexOf(labelFilter.toLowerCase()) !== -1;
        });
        state.hoverNodes = new Set(matched);
      }

      updateEdges();
      sigma.refresh();
    }
  }, [sigma, graph, currentState, nodes, filteredIndexRanges, labelSpacing, labelFilter]);

  const handleEdgeEnter = useCallback(({edge}) => {
    if (isAnimatingRef.current || selectionState.isActive || labelFilter) {
      return;
    }
    state.hoverEdges = new Set([edge]);
    state.hoverNodes = new Set([graph.source(edge), graph.target(edge)]);
    updateEdges();
    sigma.refresh();
  }, [sigma, graph, labelFilter]);

  const handleEdgeLeave = useCallback(({edge}) => {
    if (isAnimatingRef.current || selectionState.isActive || labelFilter) {
      return;
    }
    state.hoverEdges = state.hoverNodes = undefined;
    state.hoveredColumn = undefined; // Clear hovered column
    updateEdges();
    sigma.refresh();
  }, [sigma, labelFilter]);

  const handleEdgeClick = useCallback(({edge}) => {
    if (isAnimatingRef.current) return;
    Analytics.event(Analytics.EVENTS.GRAPH_EDGE_CLICK, {
      source: graph.source(edge),
      target: graph.target(edge),
    });
    state.selectedNodeIDs = new Set([graph.source(edge), graph.target(edge)]);
    state.selectedEdges = new Set([edge]);
    // Clear hover states when selecting an edge
    state.hoverNodes = state.hoverEdges = undefined;
    updateEdges();
    sigma.refresh();
  }, [sigma, graph]);

  const handleStageClick = useCallback(({event}) => {
    if (isAnimatingRef.current) return;
    const graphPos = sigma.viewportToGraph(event);
    const clickedRank = graphPos.y / yMultiplier(width, height);

    // Map click position to the visually closest column (not uniform grid)
    // This accounts for hidden columns and special 2-column layout
    const isColumnHidden = (col) => {
      return col === similarityColumn && !showSimilarity
             || col === intermediateColumn && !showIntermediate;
    };
    const visibleColumns = [];
    for (let col = 0; col < columnCount; col++) {
      if (!isColumnHidden(col)) {
        visibleColumns.push(col);
      }
    }

    // Calculate visual X position for each visible column using same logic as deriveX
    const multiplier = xMultiplier(width, height);
    const getVisualX = (columnIndex) => {
      const visualIndex = visibleColumns.indexOf(columnIndex);
      if (visualIndex === -1) return null; // Hidden column

      if (visibleColumns.length <= 1) {
        return multiplier / 2;
      }
      if (visibleColumns.length === 2) {
        return visualIndex === 0 ? 0 : multiplier * 0.72;
      }
      return (visualIndex / (visibleColumns.length - 1)) * multiplier;
    };

    // Find closest visible column to click position
    let column = -1;
    let minDistance = Infinity;
    visibleColumns.forEach(col => {
      const visualX = getVisualX(col);
      const distance = Math.abs(graphPos.x - visualX);
      if (distance < minDistance) {
        minDistance = distance;
        column = col;
      }
    });

    const filteredColumns = currentState?.getFilteredColumns() || new Set();
    console.log('[StageClick] Click detected:', {
      graphPos: { x: graphPos.x.toFixed(2), y: graphPos.y.toFixed(2) },
      clickedRank: (clickedRank * 1000) / 1000,
      detectedColumn: column,
      minDistance: minDistance.toFixed(2),
      filteredColumns: Array.from(filteredColumns),
      isFiltered: filteredColumns.has(column),
    });

    // Always clear selections on stage click
    state.hoverEdges = state.hoverNodes = state.selectedEdges = state.selectedNodeIDs = undefined;
    state.primarySelectedNode = state.primaryHoverNode = undefined;
    state.hoveredColumn = undefined; // Clear hovered column

    // Restore label filter state if active (prevent stage click from clearing filter)
    if (labelFilter !== "") {
      const matched = graph.nodes().filter(id => {
        return graph.getNodeAttribute(id, "label")?.toLowerCase().indexOf(labelFilter.toLowerCase()) !== -1;
      });
      state.hoverNodes = new Set(matched);
    }

    // Determine what to reset based on click location and current filter state
    if (clickedRank < -0.1 || clickedRank > 1.1 || column >= columnCount || column < 0) {
      // Clicked outside valid graph area (margins/legend/etc.) - clear all filters
      console.log('[StageClick] Outside valid area - clearing all filters');
      clearFilter();
    }
    else if (filteredColumns.has(column)) {
      // Clicked on a filtered column - reset just that column
      console.log('[StageClick] Clicked on filtered column', column, '- resetting it');
      clearFilter(column);
    } else {
      console.log('[StageClick] Clicked on unfiltered column', column, '- no action');
    }
    // Note: If clicking on an unfiltered column while other columns are filtered,
    // do nothing (allows user to inspect unfiltered columns without resetting)

    updateEdges();
    sigma.refresh();
  }, [sigma, columnCount, similarityColumn, showSimilarity, intermediateColumn, showIntermediate, width, height, xMultiplier, yMultiplier, clearFilter, labelFilter, graph]);

  const orderEdgeDetailNodes = (id1, id2) => {
    const col1 = graph.getNodeAttribute(id1, "column");
    const col2 = graph.getNodeAttribute(id2, "column");
    return col1 < col2 ? [id1, id2] : [id2, id1];
  }

  const handleEdgeSelect = edge => {
    if (onDetailTargetChange) {
      if (edge) {
        // Check if edge exists in graph before trying to access it
        if (!graph.hasEdge(edge)) {
          console.warn(`Edge ${edge} not found in graph, ignoring selection`);
          return;
        }

        const edgeAttrs = graph.getEdgeAttributes(edge);
        const [sourceId, targetId] = orderEdgeDetailNodes(graph.source(edge), graph.target(edge));

        // Check if this is an indirect edge with multiple collapsed paths
        if (edgeAttrs.isIndirect && edgeAttrs.collapsedCount > 1) {
          // Pass special attributes to indicate multiple paths
          onDetailTargetChange(sourceId, targetId, {
            ...edgeAttrs,
            multipleIndirectPaths: true,
            pathCount: edgeAttrs.collapsedCount,
          });
        } else {
          // Normal edge or single indirect path
          onDetailTargetChange(sourceId, targetId, edgeAttrs);
        }
      }
      else {
        onDetailTargetChange();
      }
    }
  };

  const handleDownNode = ({node}) => {
    if (isAnimatingRef.current) return;
    sigma.camera.disable();
    state.draggedNode = node;
    state.hoverNodes = state.hoverEdges = undefined;
    state.hoveredColumn = undefined; // Clear hovered column
    if (!state.selectedNodeIDs?.has(node)) {
      state.selectedNodeIDs = state.selectedEdges = undefined;
    }
    updateEdges();
    sigma.refresh();
  };
  const handleMouseUp = (e) => {
    if (state.draggedNode) {
      state.draggedNode = null;
      sigma.refresh();
    }
    sigma.camera.enable();
  };
  const handleMouseMove = e => {
    if (state.draggedNode) {
      const pos = sigma.viewportToGraph(e);

      // Constrain dragging to prevent graph shrinking from extreme positions
      // Get the node's target position from layout
      const nodeData = nodes.find(n => n.id === state.draggedNode);
      if (nodeData && currentState) {
        const targetX = deriveX(nodeData, currentState);
        const targetY = deriveY(nodeData, currentState);
        const xRange = xMultiplier(width, height);
        const yRange = yMultiplier(width, height);

        // Allow dragging within 20% of the graph dimensions from target position
        const maxDragDistance = 0.2;
        const maxDeltaX = xRange * maxDragDistance;
        const maxDeltaY = yRange * maxDragDistance;

        // Constrain position to stay within allowed range
        const newX = Math.max(targetX - maxDeltaX, Math.min(targetX + maxDeltaX, pos.x));
        const newY = Math.max(targetY - maxDeltaY, Math.min(targetY + maxDeltaY, pos.y));

        graph.setNodeAttribute(state.draggedNode, "x", newX);
        graph.setNodeAttribute(state.draggedNode, "y", newY);
      } else {
        // Fallback if node data not found or state not ready
        graph.setNodeAttribute(state.draggedNode, "x", pos.x);
        graph.setNodeAttribute(state.draggedNode, "y", pos.y);
      }

      e.preventSigmaDefault();
      e.original.preventDefault();
      e.original.stopPropagation();
      sigma.refresh();
    }
  };
  useEventHandlers({
                     downNode: handleDownNode,
                     mouseup: handleMouseUp,
                     mousemove: handleMouseMove,
                     clickNode: handleNodeClick,
                     enterNode: handleNodeEnter,
                     leaveNode: handleNodeLeave,
                     clickEdge: handleEdgeClick,
                     enterEdge: handleEdgeEnter,
                     leaveEdge: handleEdgeLeave,
                     clickStage: handleStageClick,
                   });

  // Pass state information to hover renderer through custom property on sigma instance
  // In Sigma 3.x, custom properties cannot be set via setSetting()
  // Instead, we attach custom state to the sigma instance and pass a reference through settings
  useEffect(() => {
    // Store custom state on sigma instance for easy access
    sigma.customRendererState = {
      primaryHoverNode: state.primaryHoverNode,
      hoverNodes: state.hoverNodes,
      currentHoveredNode: state.primaryHoverNode,
      graph: graph,  // Pass graph reference for hover renderer to find cluster representatives
      currentState: currentState,  // Pass current state for hover renderer to access nodeAggregates
      sigma: sigma,  // Pass sigma instance for coordinate transformations
      firstNodeKey: nodes.length > 0 ? nodes[0].id : null,  // First node for debug marker
    };

    // Update the settings to include a reference to the custom state
    // This allows renderers to access it via settings.customRendererState
    sigma.setSetting("customRendererState", sigma.customRendererState);

    sigma.refresh();
  }, [sigma, state.primaryHoverNode, state.hoverNodes, graph, currentState, nodes]);

  // ============================================================================
  // Custom Rendering: afterRender handler REMOVED
  // ============================================================================
  // Previously used afterRender to draw highlighted edges and hover nodes on top of everything.
  // Now that we have NoOpNodeProgram preventing Sigma's automatic hover rendering,
  // the base renderer (drawNodeLabel.js) and custom hover renderer (drawHoveredNodeLabel.js)
  // can handle all rendering without conflicts.

  // ============================================================================
  // Node Appearance Derivation (Pure Function of GraphState)
  // ============================================================================
  // Given a node ID and current graph state, derive all visual properties
  // NOTE: This function is currently unused but kept for potential future refactoring
  // to make nodeReducer more declarative
  const deriveNodeAppearance = useCallback((id, node, graphState) => {
    if (!graphState) {
      return { size: DEFAULT_NODE_SIZE, label: '', showLabel: false };
    }

    // Get aggregate info from state
    const aggregate = graphState.getAggregate(id);
    const aggregateCount = aggregate.count;

    // Calculate size based on aggregate count
    let size = DEFAULT_NODE_SIZE;
    if (node.column === 0) {
      // Query column: scale by degree (rank)
      const minQuerySize = 8;
      const maxQuerySize = 15;
      const rank = node.rank || 0;
      size = minQuerySize + (maxQuerySize - minQuerySize) * rank;
    } else if (aggregateCount > 1) {
      // Non-query columns with aggregates: log scaling
      const magnitudeOrder = Math.log2(aggregateCount);
      const sizeMultiplier = 1.0 + (magnitudeOrder * 0.5);
      const labelDigits = String(aggregateCount).length;
      const minSizeForLabel = Math.max(8, 3 * labelDigits + 4);
      size = Math.max(minSizeForLabel, Math.min(20, DEFAULT_NODE_SIZE * sizeMultiplier));
    }

    // Check if this node should show a label (from cluster labeling)
    const hasLabel = Array.from(graphState.nodeAggregates.keys()).includes(id) ||
                     (aggregateCount === 1 && graphState.nodeAggregates.size === 0);

    // Determine label text
    let label = '';
    let textLabel = undefined;
    if (aggregateCount > 1) {
      // Cluster: show count
      label = String(aggregateCount);
      const othersCount = aggregateCount - 1;
      const originalLabel = node.title || node.label || '';
      const isLeftmost = node.column === 0;
      const isRightmost = node.column === columnCount - 1;
      const maxLen = isRightmost ? graphState.columnLabelWidths.right
                    : isLeftmost ? graphState.columnLabelWidths.left
                    : graphState.columnLabelWidths.middle;
      const suffixOnly = `+${othersCount}`;
      const maxLabelLen = Math.max(10, maxLen - suffixOnly.length);
      if (originalLabel.length > maxLabelLen) {
        textLabel = smartTruncate(originalLabel, maxLabelLen) + suffixOnly;
      } else {
        textLabel = originalLabel + ` + ${othersCount}`;
      }
    } else {
      // Single node: use original label
      const originalLabel = node.title || node.label || '';
      const isLeftmost = node.column === 0;
      const isRightmost = node.column === columnCount - 1;
      const maxLen = isRightmost ? graphState.columnLabelWidths.right
                    : isLeftmost ? graphState.columnLabelWidths.left
                    : graphState.columnLabelWidths.middle;
      if (originalLabel.length > maxLen) {
        label = smartTruncate(originalLabel, maxLen);
      } else {
        label = originalLabel;
      }
    }

    return { size, label, textLabel, showLabel: hasLabel, aggregateCount };
  }, [columnCount]);

  useEffect(() => {
    sigma.setSetting("nodeReducer", (id, data) => {
    const props = {...data};

    // Hide all anchor nodes (RHS, top, bottom) that force graph bounds
    if (id === '__anchor_rhs__' || id === '__anchor_top__' || id === '__anchor_bottom__') {
      props.hidden = true;
      props.label = '';
      return props;
    }

    // PERFORMANCE: During randomization, skip ALL expensive logic and just show nodes
    if (isRandomizingRef.current) {
      // Check visibility during randomization
      const node = nodeByIdRef.current.get(id);
      if ((currentState && !currentState.isNodeVisible(node)) || (currentState && !currentState.isColumnVisible(props.column))) {
        props.hidden = true;
      }
      props.label = "";  // No labels during randomization
      return props;
    }

    const dragged = state.draggedNode === id;
    const selected = state.selectedNodeIDs?.has(id);
    const hover = state.hoverNodes?.has(id);
    // Also consider cluster representative as "hovered" when any of its members are hovered
    const isClusterRepresentativeHovered = id === state.clusterRepresentative && state.hoverNodes && state.hoverNodes.size > 0;
    const fade = !selected && !hover && !isClusterRepresentativeHovered && !dragged && (state.selectedNodeIDs || state.hoverNodes || state.draggedNode);

    // Declarative animation direction check: compare State A → State B
    const isAnimating = viewStateRef.current.isAnimating();
    const prevState = viewStateRef.current.previousGraphState;
    const targetState = viewStateRef.current.targetGraphState;

    // Check if column is hidden (use appropriate state based on animation)
    // During animation:
    //   - For disappearing nodes: use State A (keep visible during fade-out)
    //   - For appearing nodes: use State B (make visible during fade-in)
    // After animation: use current state
    let hide = false;
    if (isAnimating && prevState && targetState) {
      // Check if node is appearing or disappearing
      const visibleInA = prevState.isColumnVisible(props.column);
      const visibleInB = targetState.isColumnVisible(props.column);

      if (visibleInA && !visibleInB) {
        // Disappearing: use State A (keep visible during animation)
        hide = false;
      } else if (!visibleInA && visibleInB) {
        // Appearing: use State B (make visible during animation)
        hide = false;
      } else {
        // Moving: use State A
        hide = !visibleInA;
      }
    } else {
      // Not animating: use current state
      hide = currentState ? !currentState.isColumnVisible(props.column) : false;
    }

    // Track if this node is the PRIMARY directly interacted node (not just connected)
    const isPrimaryNode = dragged || id === state.primaryHoverNode || id === state.primarySelectedNode;

    // Check if node is in visible set (for filtering)
    // Keep nodes visible while they're animating out (declarative check using GraphState snapshots)
    const nodeData = nodeByIdRef.current.get(id);
    const node = nodeData; // Alias for consistency with existing code

    let isAnimatingOut = false;
    let isAnimatingIn = false;
    if (isAnimating && nodeData && prevState && targetState) {
      const wasVisible = prevState.isNodeVisible(nodeData);
      const willBeVisible = targetState.isNodeVisible(nodeData);
      isAnimatingOut = wasVisible && !willBeVisible;
      isAnimatingIn = !wasVisible && willBeVisible;
    }

    // Check if node should be hidden (not visible in current state and not animating)
    // Keep visible if animating out (fade out) OR animating in (fade in)
    if (currentState && !currentState.isNodeVisible(nodeData) && !isAnimatingOut && !isAnimatingIn) {
      props.hidden = true;
      return props;
    }

    // Check if node has any visible connections (not hidden by similarity/intermediate filters)
    // Applied globally - ordinarily affects query terms which can become isolated
    let hasVisibleConnections = false;
    try {
      const neighbors = graph.neighbors(id);
      for (const nbr of neighbors) {
        const nbrColumn = graph.getNodeAttribute(nbr, "column");
        const nbrHidden = currentState ? !currentState.isColumnVisible(nbrColumn) : false;
        if (!nbrHidden) {
          hasVisibleConnections = true;
          break;
        }
      }
    } catch (e) {
      hasVisibleConnections = true; // Assume connected if we can't determine
    }

    // Hide disconnected target nodes (rightmost column) when in indirect mode
    const isIndirectMode = queryCategory === "compound" && !showSimilarity;
    const isTargetColumn = props.column === columnCount - 1;
    if (isIndirectMode && isTargetColumn && !hasVisibleConnections && !selected && !hover) {
      props.hidden = true;
      return props;
    }

    // Check if node is interactive for label purposes (includes all selected/hovered/dragged)
    const isInteractive = selected || hover || dragged;

    // SIZE CALCULATION: Animated nodes use animated size, all others calculate from state
    if (viewStateRef.current.animatedNodeSizes.has(id)) {
      // This node is animating: use animated size (animation has exclusive control)
      const animatedSize = viewStateRef.current.animatedNodeSizes.get(id);
      props.size = Math.max(0.01, animatedSize);
    } else if (currentState) {
      // STEADY STATE: Calculate from currentState
      const isQueryColumn = props.column === 0;

      if (isQueryColumn) {
        // Query nodes: ALWAYS scale by degree (rank represents normalized degree 0-1)
        // Both interactive and non-interactive query nodes use degree-based sizing
        // This is the ORIGINAL size for query nodes (before any aggregation)
        const minQuerySize = 8;
        const maxQuerySize = 15;
        const rank = node?.rank || 0;
        props.size = minQuerySize + (maxQuerySize - minQuerySize) * rank;
      } else if (isInteractive) {
        // Non-query interactive nodes (hover/click/drag)
        // For cluster nodes: keep aggregate size (don't shrink on hover)
        // For single nodes: use ORIGINAL size (DEFAULT_NODE_SIZE before aggregation)
        // CRITICAL: Check aggregates in priority order (matches label rendering priority)
        // Priority: labelSpacingAggregates > hoverAggregates > selectedAggregates > currentState
        let aggregate;
        if (state.labelSpacingAggregates && state.labelSpacingAggregates.has(id)) {
          // Label spacing changed - use new aggregates immediately
          aggregate = state.labelSpacingAggregates.get(id);
        } else if (hover && state.hoverAggregates && state.hoverAggregates.has(id)) {
          // Use hover-time aggregate (from hover relabeling)
          aggregate = state.hoverAggregates.get(id);
        } else if (selected && state.selectedAggregates && state.selectedAggregates.has(id)) {
          // Use selected aggregate (from click on single node)
          aggregate = state.selectedAggregates.get(id);
        } else {
          // Use static aggregate from currentState
          aggregate = currentState.getAggregate(id);
        }

        if (aggregate.count > 1) {
          // Cluster node: keep aggregate size to prevent flicker
          const aggregateCount = aggregate.count;
          const magnitudeOrder = Math.log2(aggregateCount);
          const sizeMultiplier = 1.0 + (magnitudeOrder * 0.5);
          const labelDigits = String(aggregateCount).length;
          const minSizeForLabel = Math.max(8, 3 * labelDigits + 4);
          props.size = Math.max(minSizeForLabel, Math.min(20, DEFAULT_NODE_SIZE * sizeMultiplier));
        } else {
          // Single node: use original size
          props.size = DEFAULT_NODE_SIZE;
        }
      } else {
        // Non-query, non-interactive nodes: scale by aggregate count from state
        const aggregate = currentState.getAggregate(id);
        const aggregateCount = aggregate.count;

        if (aggregateCount > 1) {
          // Aggregate: logarithmic size multiplier
          const magnitudeOrder = Math.log2(aggregateCount);
          const sizeMultiplier = 1.0 + (magnitudeOrder * 0.5);
          const labelDigits = String(aggregateCount).length;
          const minSizeForLabel = Math.max(8, 3 * labelDigits + 4);
          props.size = Math.max(minSizeForLabel, Math.min(20, DEFAULT_NODE_SIZE * sizeMultiplier));
        } else {
          // Single node: base size
          props.size = DEFAULT_NODE_SIZE;
        }
      }
    } else {
      // Fallback: base size
      props.size = DEFAULT_NODE_SIZE;
    }

    // Apply animated opacity for nodes fading in/out
    if (viewStateRef.current.animatedNodeOpacities.has(id)) {
      const opacity = viewStateRef.current.animatedNodeOpacities.get(id);
      props.color = applyOpacity(props.color || NODE_COLOR_DEFAULT, opacity);

      // Hide nodes only when opacity is exactly 0 (fully faded out)
      // Don't use threshold - it interferes with smooth fade-in
      if (opacity === 0) {
        props.hidden = true;
        return props; // Early return - no need to process further
      }
    }
    // ============================================================================
    // NODE STYLING: Declarative property assignment based on state
    // ============================================================================
    // Determine styling independently based on node state, avoiding complex if-else chains.
    // Priority order: hide > drag > hover > selected > fade/disconnected

    // HIDDEN: Hide node completely (highest priority)
    if (hide) {
      props.hidden = true;
    }

    // COLOR: Determine node color based on interaction state
    // Priority: dragged > hover/clusterRepresentativeHovered > selected > fade > disconnected
    if (dragged) {
      props.color = NODE_COLOR_SELECTED;
      props.highlighted = true;
    } else if (hover || isClusterRepresentativeHovered) {
      props.color = NODE_COLOR_HOVER;
      props.highlighted = true;
    } else if (selected) {
      props.color = NODE_COLOR_SELECTED;
      props.highlighted = true;
    } else if (fade && props.color) {
      // Apply fade color (needs hex colors, not rgba)
      props.color = fadeColor(props.color);
    } else if (!hasVisibleConnections && !isAnimatingOut) {
      // Apply disconnected opacity to non-interactive nodes
      props.color = applyOpacity(props.color || NODE_COLOR_DEFAULT, NODE_OPACITY_DISCONNECTED);
    }

    // Z-INDEX: Ensure highlighted nodes render on top of non-highlighted nodes
    // Priority: dragged (highest) > hover > selected > faded (below highlighted edges) > normal
    if (dragged) {
      props.zIndex = 3;  // Dragged nodes on top
    } else if (hover) {
      props.zIndex = 2;  // Hover nodes above normal
    } else if (selected) {
      props.zIndex = 2;  // Selected nodes above normal
    } else if (fade) {
      props.zIndex = 0;  // Faded nodes BELOW highlighted edges (which use z=5 for selected, z=10 for hover)
    } else {
      props.zIndex = 1;  // Normal nodes at base level
    }
    // ============================================================================
    // LABEL RENDERING: Independent Property Assignment Based on State
    // ============================================================================
    // Each property (clusterCount, clusterDescription, nodeTitle) is set independently
    // based on simple state conditions, avoiding complex nested if/else chains.

    // Step 1: Determine all helper values and state conditions
    const isInHoveredColumn = state.hoveredColumn !== undefined && state.hoveredColumn === props.column;
    // For label filter: never hide labels on matching nodes (hover=true means it matched the filter)
    const shouldHideAllLabels = (width <= 400 || !currentState || fade) && !hover;
    // Hide text labels in hovered column EXCEPT for:
    // 1. The primary hovered node (isPrimaryNode)
    // 2. The cluster representative when any cluster member is hovered (isClusterRepresentative && hover)
    const isClusterRepresentative = id === state.clusterRepresentative;
    const shouldHideTextLabels = isInHoveredColumn && !isPrimaryNode && !(isClusterRepresentative && hover);

    // Determine which state to use for non-interactive nodes (animation handling)
    // CRITICAL: For cross-fade animations, use State A during fade-out (first half)
    // and State B during fade-in (second half). The text switches at the midpoint.
    let stateToUse = currentState;
    if (!isInteractive && isAnimating && prevState && targetState) {
      // Use animationProgress (0-1) to determine which half of the animation we're in
      // This is more reliable than checking opacity because cross-fade opacity goes 1→0→1
      const animationProgress = viewStateRef.current.animationProgress || 0;
      const inSecondHalf = animationProgress >= 0.5;

      if (isAnimatingOut) {
        // Disappearing nodes: always use State A (label fades out entirely)
        stateToUse = prevState;
      } else if (isAnimatingIn) {
        // Appearing nodes: always use State B (label fades in)
        stateToUse = targetState;
      } else {
        // Moving/resizing nodes: switch label text at midpoint
        // First half (progress 0-0.5): show State A labels while fading out
        // Second half (progress 0.5-1): show State B labels while fading in
        if (inSecondHalf) {
          stateToUse = targetState;
        } else {
          stateToUse = prevState;
        }
      }
    }

    // Get aggregate and label info
    // CRITICAL: Only representative nodes (keys in nodeAggregates) should show cluster count
    // Non-representative cluster members should NOT show the count - hover renderer will handle it
    // PRIORITY ORDER (rendering priority): labelSpacingAggregates > hoverAggregates > selectedAggregates > clickPreservedAggregates > currentState.nodeAggregates
    // This matches the hover pattern: temporary state in plain object > persistent React state
    // Rendering order: basic => selected => hover
    const aggregatesToUseForLabel = state.labelSpacingAggregates
      ? state.labelSpacingAggregates
      : (hover && state.hoverAggregates)
        ? state.hoverAggregates
        : (selected && state.selectedAggregates)
          ? state.selectedAggregates
          : state.clickPreservedAggregates
            ? state.clickPreservedAggregates
            : (stateToUse ? stateToUse.nodeAggregates : new Map());

    let aggregate = stateToUse ? stateToUse.getAggregate(id) : { count: 1, nodeIds: [id] };

    // Check for temporary aggregates (label spacing, hover, selected, or click-preserved) that override persistent state
    // Rendering order: basic => selected => hover
    if (state.labelSpacingAggregates && state.labelSpacingAggregates.has(id)) {
      // Label spacing changed - use new aggregates immediately
      aggregate = state.labelSpacingAggregates.get(id);
    } else if (hover && state.hoverAggregates && state.hoverAggregates.has(id)) {
      // During hover - use hover-time aggregate (highest priority for interaction)
      aggregate = state.hoverAggregates.get(id);
    } else if (selected && state.selectedAggregates && state.selectedAggregates.has(id)) {
      // During selection - use cloned hover-time aggregate (persists until cleared)
      console.log(`[NodeReducer Aggregate] Node ${id.substring(0, 20)}: USING selectedAggregates, count=${state.selectedAggregates.get(id).count}`);
      aggregate = state.selectedAggregates.get(id);
    } else if (state.clickPreservedAggregates && state.clickPreservedAggregates.has(id)) {
      // After cluster click - use preserved hover-time aggregate
      aggregate = state.clickPreservedAggregates.get(id);
    }

    let isCluster = aggregate.count > 1;

    // Debug logging for selected nodes
    if (selected && isCluster) {
      console.log(`[NodeReducer Selected] Node ${id.substring(0, 20)}: selected=${selected}, hasSelectedAggregates=${!!(state.selectedAggregates)}, aggregateCount=${aggregate.count}, source=${state.selectedAggregates?.has(id) ? 'selectedAggregates' : 'fallback'}`);
    }

    // When labelFilter is active, hover=true (without selection/hover interaction) means node matched filter
    // For filter matches: treat as non-cluster to show individual label, not cluster description
    // Check: hover but no hoverAggregates/selectedAggregates means this is from label filter
    const isLabelFilterMatch = hover && !selected && !state.hoverAggregates && !state.selectedAggregates;
    if (isLabelFilterMatch) {
      isCluster = false; // Show individual label, not cluster label
    }
    const hasLabel = aggregatesToUseForLabel.has(id) || isLabelFilterMatch;
    // node is already defined earlier from nodeData

    // Calculate label widths (for text truncation)
    const isLeftmost = props.column === 0;
    const isRightmost = props.column === columnCount - 1;

    // For animations: use future widths when fading in
    const labelOpacity = viewStateRef.current.animatedLabelOpacities.get(id);
    const useFutureWidths = labelOpacity !== undefined && labelOpacity > 0.3 &&
                            viewStateRef.current.targetGraphState;
    const widthsToUse = useFutureWidths && currentState
                        ? viewStateRef.current.targetGraphState.columnLabelWidths
                        : (currentState ? currentState.columnLabelWidths : columnLabelWidths);

    const maxLen = isRightmost ? widthsToUse.right
                  : isLeftmost ? widthsToUse.left
                  : widthsToUse.middle;

    // Get original label text
    const originalLabel = node?.title || node?.label || '';

    // Step 2: Set each property independently based on state

    // Initialize all properties to empty
    props.clusterCount = "";
    props.clusterDescription = "";
    props.nodeTitle = "";
    props.forceLabel = false;

    // clusterCount: Show ONLY for representative nodes (hasLabel = true)
    // CRITICAL: clusterCount is used by hover renderer to identify the representative
    // Non-representative members should NOT have clusterCount set
    if (isCluster && !shouldHideAllLabels && hasLabel) {
      props.clusterCount = String(aggregate.count);
    }

    // Debug logging for cluster count visibility
    if (isCluster && (hover || isPrimaryNode)) {
      console.log(`[NodeReducer] Cluster ${id.substring(0, 20)}: hover=${hover}, isPrimaryNode=${isPrimaryNode}, hasLabel=${hasLabel}, fade=${fade}, shouldHideAllLabels=${shouldHideAllLabels}, clusterCount="${props.clusterCount}"`);
    }

    // clusterDescription: Show on representative nodes
    // CRITICAL: The label should ONLY appear on the representative node
    // - Default (no hover): Show on ALL representative nodes with labels
    // - During hover: Show on representative if it's part of the hovered cluster
    const isHoverLabeled = state.hoverLabelNodes && state.hoverLabelNodes.has(id);
    // isClusterRepresentative already defined above at line 2757

    // Determine if this cluster's label should be shown
    // During hover: show label if this node is the representative AND is part of the hover set
    // This works because when ANY cluster member is hovered, ALL members are added to hoverNodes
    const shouldShowClusterLabel = hover
      ? hasLabel  // During hover: show on ANY representative that's in the hover set
      : hasLabel; // Default: show on all representative nodes

    if (isCluster && (hover || isPrimaryNode)) {
      console.log(`[NodeReducer Label] Cluster ${id.substring(0, 20)}: hover=${hover}, isPrimaryNode=${isPrimaryNode}, hasLabel=${hasLabel}, isClusterRepresentative=${isClusterRepresentative}, shouldHideAllLabels=${shouldHideAllLabels}, shouldHideTextLabels=${shouldHideTextLabels}, shouldShowClusterLabel=${shouldShowClusterLabel}, props.highlighted=${props.highlighted}`);
    }

    if (isCluster && !shouldHideAllLabels && !shouldHideTextLabels && shouldShowClusterLabel) {
      // Show descriptive label on representative node
      // During hover: isClusterRepresentative ensures this is THE representative of the hovered cluster
      // Default: hasLabel ensures this is a representative node
      const othersCount = aggregate.count - 1;
      const suffixOnly = `+${othersCount}`;
      const maxLabelLen = Math.max(10, maxLen - suffixOnly.length);

      if (originalLabel.length > maxLen) {
        props.clusterDescription = smartTruncate(originalLabel, maxLabelLen) + suffixOnly;
      } else {
        props.clusterDescription = originalLabel + ` + ${othersCount}`;
      }
    }

    // nodeTitle: Show for nodes with labels OR the primary interactive node OR hover-labeled nodes
    // CRITICAL: Skip non-representative cluster members entirely - they should NEVER show labels
    // When labelFilter is active, hover=true means node matched the filter, so show its label
    if (!isCluster && !shouldHideAllLabels && !shouldHideTextLabels) {
      // First check if this node is a non-representative cluster member
      // During hover: check against hover aggregates; otherwise check against static aggregates
      // EXCEPTION: Skip this check for label filter matches - they should always show their label
      let isSecondaryClusterMember = false;
      if (!isLabelFilterMatch) {
        const aggregatesToCheck = (hover && state.hoverAggregates) ? state.hoverAggregates : (currentState ? currentState.nodeAggregates : new Map());

        for (const [repId, agg] of aggregatesToCheck.entries()) {
          if (agg.nodeIds.includes(id) && repId !== id) {
            // This node is a non-representative member - it should NEVER show a label
            isSecondaryClusterMember = true;
            break;
          }
        }
      }

      // Only assign nodeTitle if this is NOT a secondary cluster member
      // When hover=true (including from label filter), show the label
      if (!isSecondaryClusterMember && (isPrimaryNode || hasLabel || isHoverLabeled || hover)) {
        let labelToShow = originalLabel;

        if (labelToShow.length > maxLen) {
          props.nodeTitle = smartTruncate(labelToShow, maxLen);
        } else {
          props.nodeTitle = labelToShow;
        }
      }
    }

    // forceLabel: Enable for primary interactive node, nodes with labels, hover-labeled nodes
    // CRITICAL: Skip non-representative cluster members - they should NEVER show labels
    // When labelFilter is active, hover=true means node matched the filter, so force show its label
    if (!shouldHideAllLabels) {
      // Check if this is a non-representative cluster member
      // During hover: check against hover aggregates; otherwise check against static aggregates
      // EXCEPTION: Skip this check for label filter matches - they should always show their label
      let isSecondaryClusterMember = false;
      if (!isLabelFilterMatch) {
        const aggregatesToCheck = (hover && state.hoverAggregates) ? state.hoverAggregates : (currentState ? currentState.nodeAggregates : new Map());

        for (const [repId, agg] of aggregatesToCheck.entries()) {
          if (agg.nodeIds.includes(id) && repId !== id) {
            isSecondaryClusterMember = true;
            break;
          }
        }
      }

      // Only enable forceLabel if NOT a secondary cluster member
      // When hover=true (including from label filter), force show the label
      if (!isSecondaryClusterMember && (isPrimaryNode || hasLabel || isHoverLabeled || hover)) {
        props.forceLabel = true;
      }
    }

    // Apply animated label opacity (fade labels during animation)
    if (viewStateRef.current.animatedLabelOpacities.has(id)) {
      const labelOpacity = viewStateRef.current.animatedLabelOpacities.get(id);
      props.labelOpacity = labelOpacity;
      // Clear label only when opacity is exactly 0
      // Don't use threshold - it interferes with smooth fade-in
      if (labelOpacity === 0) {
        props.label = "";
      }
    }

    return props;
  });
  sigma.setSetting("edgeReducer", (id, data) => {
    const edgeProps = {...data};

    // PERFORMANCE: During randomization, hide ALL edges for instant feedback
    // This dramatically reduces canvas rendering time
    if (isRandomizingRef.current) {
      edgeProps.hidden = true;
      return edgeProps;
    }

    const selected = state.selectedEdges && state.selectedEdges.has(id);
    const hover = state.hoverEdges && state.hoverEdges.has(id);

    // Check if this edge is connected to the dragged node
    const connectedToDragged = state.draggedNode && (
      graph.source(id) === state.draggedNode ||
      graph.target(id) === state.draggedNode
    );

    // Fade edges that are not selected, not hovered, not connected to dragged node,
    // and there's some interaction happening (selection, hover, or drag)
    const fade = !selected && !hover && !connectedToDragged &&
                 (state.selectedEdges || state.hoverEdges || state.draggedNode);

    // DECLARATIVE: Calculate edge visibility from current node columns + animation state
    // No pre-computed flags - just derive from parameters
    const isAnimating = viewStateRef.current.animatedEdgeOpacities.has(id);

    let sourceColumn = null;
    let targetColumn = null;
    let hasHiddenColumn = false;

    try {
      const source = graph.source(id);
      const target = graph.target(id);
      sourceColumn = graph.getNodeAttribute(source, "column");
      targetColumn = graph.getNodeAttribute(target, "column");

      // Check if column is hidden (edges with both endpoints in hidden columns should be hidden)
      const sourceHidden = currentState ? !currentState.isColumnVisible(sourceColumn) : false;
      const targetHidden = currentState ? !currentState.isColumnVisible(targetColumn) : false;

      // Edge should only be hidden if BOTH endpoints are in hidden columns
      // If only one endpoint is hidden, the edge spans a hidden column and should be shown as indirect
      hasHiddenColumn = sourceHidden && targetHidden;
    } catch (e) {
      // Edge might not exist in graph
      hasHiddenColumn = false;
    }

    // CRITICAL: Keep edges visible if they're animating (fading)
    // This prevents "detached edge" visual artifact during column collapse
    if (isAnimating) {
      // Edge is animating - don't hide it, let the opacity animation handle visibility
      // The animated opacity is applied later (line 2559-2562)
    } else {
      // Edge is not animating - check if it should be hidden for other reasons
      const hide = (
        hasHiddenColumn ||  // Hide ONLY if BOTH endpoints are in hidden columns
        (!selected && !hover && state.selectedEdges) ||
        selectionState.isActive ||
        labelFilter
      );

      if (hide) {
        edgeProps.hidden = true;
      }
    }

    // Determine if this edge is indirect (synthetic edge created client-side)
    // Indirect edges represent collapsed multi-hop paths when intermediate columns are hidden
    const isIndirect = edgeProps.isIndirect || false;

    // Check if this edge has animated opacity (will be applied later)
    const hasAnimatedOpacity = viewStateRef.current.animatedEdgeOpacities.has(id);

    // Apply base styling based on edge type (indirect vs direct)
    // This is applied FIRST, then hover/click overrides it
    if (isIndirect) {
      // Indirect edges: synthetic edges representing collapsed multi-hop paths
      // They aggregate many different edges, so they should NOT have labels
      // If animating, use solid color and let animated opacity control fade
      // Otherwise, use base opacity
      if (hasAnimatedOpacity) {
        edgeProps.color = EDGE_COLOR_INDIRECT; // Solid color, animated opacity applied later
      } else {
        edgeProps.color = applyOpacity(EDGE_COLOR_INDIRECT, EDGE_OPACITY_INDIRECT);
      }
      edgeProps.size = EDGE_SIZE_INDIRECT;
      edgeProps.zIndex = 1; // Normal edges at base level
      edgeProps.label = ''; // Never show labels for indirect edges
    } else {
      // Direct edges from server (not indirect)

      // Clear labels for edges that span hidden columns
      // These edges may have labels like "similarity analysis" that should not show
      // when the intermediate column is hidden
      if (sourceColumn !== null && targetColumn !== null) {
        const minCol = Math.min(sourceColumn, targetColumn);
        const maxCol = Math.max(sourceColumn, targetColumn);
        // Check if ANY column between source and target is hidden
        for (let col = minCol + 1; col < maxCol; col++) {
          if (currentState && !currentState.isColumnVisible(col)) {
            // Edge spans hidden column - clear its label
            edgeProps.label = '';
            break;
          }
        }
      }

      // Direct edges keep their default color/opacity from edgeColor calculation
      // (based on details score). This preserves the opacity scale showing
      // edge strength even when columns are hidden.

      edgeProps.zIndex = 1; // Normal edges at base level
    }

    // Apply hover/click styling LAST to override base styling
    // This ensures interactive edges (hovered or selected) get proper styling
    // regardless of their type (indirect, direct, etc.)
    if (hover) {
      // Hover styling overrides everything
      edgeProps.hidden = false; // Unhide if previously hidden
      edgeProps.highlighted = true;
      edgeProps.size = EDGE_SIZE_HOVER;
      edgeProps.color = EDGE_COLOR_HOVER;
      edgeProps.zIndex = 10; // Hovered edges ABOVE all nodes (nodes use z-index 1-3)
      edgeProps.forceLabel = true; // Ensure labels are visible
    } else if (selected) {
      // Selected styling
      edgeProps.hidden = false; // Unhide if previously hidden
      edgeProps.highlighted = true;
      edgeProps.color = EDGE_COLOR_SELECTED;
      edgeProps.size = EDGE_SIZE_SELECTED;
      edgeProps.zIndex = 5; // Selected edges above normal nodes (nodes use z-index 1-3)
      edgeProps.forceLabel = true;
    }

    // Apply animated opacity for edges connected to removed nodes
    if (viewStateRef.current.animatedEdgeOpacities.has(id)) {
      let opacity = viewStateRef.current.animatedEdgeOpacities.get(id);

      // For indirect edges, multiply animated opacity by their target opacity (0.1)
      // This fades them from 0 → 10% instead of 0 → 100%
      if (isIndirect) {
        opacity = opacity * EDGE_OPACITY_INDIRECT;
      }

      edgeProps.color = applyOpacity(edgeProps.color || EDGE_COLOR_DEFAULT, opacity);

      // Hide edges only when opacity is exactly 0 (fully faded out)
      // Don't use threshold - it interferes with smooth fade-in
      if (opacity === 0) {
        edgeProps.hidden = true;
      }
    }

    if (fade) {
      edgeProps.label = "";
      // Hide faded edges completely when there's a selection/hover
      // This prevents them from obscuring selected edges
      edgeProps.hidden = true;
    }

    // For indirect edges, check if ALL skipped columns are hidden
    // If any skipped column is visible, hide the edge (the direct path exists)
    // This check comes AFTER styling to ensure hover/click edges get styled before hiding
    // CRITICAL: For ANIMATING edges, use State A to keep them hidden initially during fade-in
    // For NON-ANIMATING edges (already visible), use target state so they don't disappear
    if (isIndirect && sourceColumn !== null && targetColumn !== null && !hover && !selected) {
      const minCol = Math.min(sourceColumn, targetColumn);
      const maxCol = Math.max(sourceColumn, targetColumn);

      // During animation: use target state (State B) for visibility check
      // This ensures edges that should be visible in the final state are treated as visible
      // The animated opacity controls the fade for edges that are appearing
      // After animation completes: use current state
      const stateToCheck = isAnimating ? viewStateRef.current.targetGraphState : currentState;

      // Check if ANY column between source and target is visible
      for (let col = minCol + 1; col < maxCol; col++) {
        if (stateToCheck && stateToCheck.isColumnVisible(col)) {
          // Skipped column is visible - hide this indirect edge
          edgeProps.hidden = true;
          break;
        }
      }
    }

    return edgeProps;
  });
  }, [sigma, graph, nodes, currentState, columnCount, showSimilarity, queryCategory, similarityColumn, intermediateColumn, showIntermediate, columnLabelWidths, labelFilter]);

  // Use fullAdjacency directly - all edges already added
  const visibleAdjacency = fullAdjacency;

  // Callback to trigger animation - self-contained animation that bypasses the useEffect
  const triggerAnimation = useCallback(() => {
    // Guard: don't randomize if state isn't ready
    if (!currentState) {
      console.warn('[RANDOMIZE] Skipped - currentState not ready');
      return;
    }

    console.log('[RANDOMIZE] Starting...');

    // Set flag to prevent the main animation useEffect from interfering
    isAnimatingRef.current = true;
    setIsAnimating(true);
    onAnimationChange?.(true); // Notify parent that animation started
    // Set flag to skip expensive rendering (like labels) during randomization
    isRandomizingRef.current = true;

    // Randomize should preserve filtering state (scaledRanks are in GraphState)
    // Only randomize positions, not clear the filter

    // Randomize immediately - apply positions in one efficient pass
    const xRange = xMultiplier(width, height);
    const yRange = yMultiplier(width, height);
    const nodesToAnimate = [];

    // Derive filtered nodes from current state for randomization
    const filteredNodes = nodes.filter(n => currentState && currentState.isNodeVisible(n));

    // Apply randomized positions in a single loop
    filteredNodes.forEach(n => {
      nodesToAnimate.push(n.id);
      graph.setNodeAttribute(n.id, "x", Math.random() * xRange);
      graph.setNodeAttribute(n.id, "y", Math.random() * yRange);
    });

    console.log(`[RANDOMIZE] Randomized ${nodesToAnimate.length} nodes, refreshing...`);

    // Refresh immediately to show randomized positions
    sigma.refresh();
    console.log('[RANDOMIZE] Displayed randomized positions!');

    // Use requestAnimationFrame to ensure browser has painted, then wait 500ms before animating
    requestAnimationFrame(() => {
      setTimeout(() => {
        console.log('[RANDOMIZE] Calculating targets and starting animation...');

      // Now calculate target positions for animation
      const animations = [];
      nodesToAnimate.forEach(id => {
        const node = nodeByIdRef.current.get(id);
        if (node && graph.hasNode(id)) {
          const startX = graph.getNodeAttribute(id, "x");
          const startY = graph.getNodeAttribute(id, "y");
          const targetX = deriveX(node, currentState);
          const targetY = deriveY(node, currentState);

          // Debug logging for first few nodes
          if (animations.length < 3) {
            console.log(`[RANDOMIZE] Node ${id} (col=${node.column}): startX=${startX.toFixed(0)}, targetX=${targetX.toFixed(0)}, deltaX=${(targetX - startX).toFixed(0)}`);
          }

          animations.push({
            id,
            startX,
            startY,
            targetX,
            targetY
          });
        }
      });

      const nodeDuration = ANIMATION_DURATION;  // Node position animation duration
      const nodeStartTime = performance.now();

      const animateNodes = (currentTime) => {
        const elapsed = currentTime - nodeStartTime;
        const progress = Math.min(elapsed / nodeDuration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out

        animations.forEach(({id, startX, startY, targetX, targetY}) => {
          const newX = startX + (targetX - startX) * easeProgress;
          const newY = startY + (targetY - startY) * easeProgress;
          graph.setNodeAttribute(id, "x", newX);
          graph.setNodeAttribute(id, "y", newY);
        });

        sigma.refresh();

        if (progress < 1) {
          requestAnimationFrame(animateNodes);
        } else {
          // Animation complete - show edges and labels
          console.log('[RANDOMIZE] Complete');
          isRandomizingRef.current = false;
          isAnimatingRef.current = false;
          setIsAnimating(false);
          onAnimationChange?.(false); // Notify parent that animation ended
          // Labels will reappear via state initialization effect
          sigma.refresh();
        }
      };

      requestAnimationFrame(animateNodes);
    }, 0);
    });
  }, [nodes, width, height, graph, sigma, xMultiplier, yMultiplier, deriveX, deriveY, currentState]);

  // LEGACY CLUSTER LABELING SYSTEM - REMOVED
  // This system has been replaced by the declarative computeGraphState() function
  // which computes aggregates as part of the GraphState snapshot.
  // See git history for the old 200+ line implementation.

  // Calculate which categories are present in VISIBLE columns (for legend)
  // Sort by column order to match visual layout
  const visibleCategories = useMemo(() => {
    // Map category keys to display names
    const categoryLabels = {
      compound: 'Compound',
      similarCompound: 'Similar Compound',
      target: 'Target',
      disease: 'Disease',
      faers: 'Adverse Event',
      publication: 'Publication',
      patent: 'Patent',
      geneset: 'Gene Set',
      moa: 'Mechanism of Action',
      org: 'Organization',
    };

    // Group nodes by column, collecting ALL distinct categories per column
    const columnCategories = new Map(); // Map<column, Set<category>>
    nodes.forEach(n => {
      if (!columnCategories.has(n.column)) {
        columnCategories.set(n.column, new Set());
      }
      columnCategories.get(n.column).add(n.category);
    });

    // Sort by column index to match visual order (left to right)
    // Filter out hidden columns
    const sortedColumns = Array.from(columnCategories.entries())
      .filter(([column]) => !currentState || currentState.isColumnVisible(column))
      .sort((a, b) => a[0] - b[0]); // Sort by column index

    // Build array of {category, color, label} for legend in column order
    // Each column may contribute multiple entries if it has multiple categories
    return sortedColumns.flatMap(([column, cats]) => {
      // Special handling for similarity column - show as "Similar <QueryCategory>"
      if (column === similarityColumn && similarityColumn >= 0) {
        const baseLabel = categoryLabels[queryCategory] || queryCategory;
        return [{
          category: 'similarCompound',
          color: NODE_COLOR.similarCompound,
          label: `Similar ${baseLabel}`,
        }];
      }

      return Array.from(cats).map(cat => ({
        category: cat,
        color: NODE_COLOR[cat] || NODE_COLOR_DEFAULT,
        label: categoryLabels[cat] || cat,
      }));
    }).filter((entry, idx, arr) => arr.findIndex(e => e.category === entry.category) === idx);
  }, [nodes, similarityColumn, queryCategory, currentState]);

  // Render warning banner, legend, and loader
  return (
    <>
      {!hasDirectConnections && (
        <div className="graph-warning-banner">
          ⚠ No direct connections found - showing only indirect paths
        </div>
      )}
      <div className="graph-info-banner">
        💡 Drag to select nodes and filter • Click filtered column to reset it • ESC to reset all
      </div>
      {visibleCategories.length > 0 && (
        <div className="graph-category-legend">
          {visibleCategories.map(({category, color, label}) => (
            <div key={category} className="graph-category-legend-item">
              <div className="graph-category-legend-dot" style={{backgroundColor: color}} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      )}
      <SigmaEdgeGraphLoader
        nodes={nodes}
        adjacency={visibleAdjacency}
        controlsPosition={"bottom-right"}
        width={width}
        height={height}
        deriveX={deriveX}
        deriveY={deriveY}
        currentState={currentState}
        setMinSimilarity={setMinSimilarity}
        setMaxSimilarity={setMaxSimilarity}
        onRandomize={triggerAnimation}
        columnCount={columnCount}
        similarityColumn={similarityColumn}
        intermediateColumn={intermediateColumn}
        showSimilarity={showSimilarity}
        showIntermediate={showIntermediate}
        disableControls={isAnimating}
      />
    </>
  );
};

export const EdgeGraph = ({
                            queryIDs = [],
                            nodesData = [],
                            adjacencyList = [],
                            columnCount = 1,
                            datasets = {},
                            queryCategory,
                            targetCategory,
                            sortBySimilarity = false,
                            labelFilter = "",
                            getNodeLabel = (id) => null,
                            ...childProps
                           }) => {

  const generateSigmaNodes = (nodes, queryIDs, targetCategory, columnCount) => {
    if (nodes.length === 0) {
      return nodes;
    }
    const qids = new Set(queryIDs || []);
    const nodeColumn = (id, category) => {
      if (qids.has(id)) {
        return 0;
      }
      if (category === queryCategory) {
        setSimilarityColumn(1);
        return 1;
      }
      const targetFamily = CATEGORY_FAMILY[targetCategory];
      if (category === targetCategory || (targetFamily && targetFamily.has(category))) {
        return columnCount - 1;
      }
      setIntermediateColumn(columnCount - 2);
      return columnCount - 2;
    };
    const nodeColor = (id, category) => {
      if (category === queryCategory && !qids.has(id)) {
        return NODE_COLOR['similarCompound'];
      }
      return NODE_COLOR[category] || NODE_COLOR_DEFAULT
    };
    return nodes.map(([rank, id, category, title, ...rest]) => {
      const label = getNodeLabel(id) || title;
      const column = nodeColumn(id, category);

      // Use default constants for initial truncation
      // (nodeReducer will apply dynamic truncation during rendering based on actual pixel spacing)
      let maxLabelLength;
      if (column === 0) {
        maxLabelLength = DEFAULT_LABEL_LENGTH_LEFT;
      } else if (column === columnCount - 1) {
        maxLabelLength = DEFAULT_LABEL_LENGTH_RIGHT;
      } else {
        maxLabelLength = DEFAULT_LABEL_LENGTH_MIDDLE;
      }

      const truncatedLabel = label && label.length > maxLabelLength
        ? label.substring(0, maxLabelLength) + "..."
        : label || "";

      return {
        id,
        // Store FULL original label in title for nodeReducer to use (not the raw title parameter)
        title: label,
        label: truncatedLabel,
        rank,
        category,
        column,
        color: nodeColor(id, category),
      };
    })
  };

  const [intermediateColumn, setIntermediateColumn] = useState(-1);
  const [similarityColumn, setSimilarityColumn] = useState(-1);

  const sigmaNodes = useMemo(() => {
    return generateSigmaNodes(nodesData, queryIDs, targetCategory, columnCount);
  }, [nodesData, queryIDs, targetCategory, columnCount]);

  const detailsScore = nbr => {
    if (!(nbr instanceof Array)) {
      return 0.5;
    }
    const [id, details] = nbr;
    const dsCount = new Set(details.map(d => d.dataset)).size;
    return Math.log10(details.length) * (1 + dsCount / 10);
  };
  const maxDetailsScore = adjacencyList
    .reduce((max, neighbors) => Math.max(max, neighbors
              .reduce((nmax, nbr) => Math.max(nmax, detailsScore(nbr)),
                      0)),
            0.0001);
  const edgeColor = nbr => {
    const n = Math.round((maxDetailsScore - detailsScore(nbr)) / maxDetailsScore * 0xE0);
    const hex = `${n < 16 ? "0" : ""}${n.toString(16)}`;
    const hexColor = `#${hex}${hex}${hex}`;
    // Apply opacity only in dark mode - light mode uses solid edges
    const isDarkMode = getTheme() === THEMES.DARK;
    return isDarkMode ? applyOpacity(hexColor, EDGE_OPACITY_DIRECT) : hexColor;
  }
  const adjacency = useMemo(() => {
    return adjacencyList.map(neighbors => neighbors.map(nbr => {
      // Neighbor may be a single node index, or a pair of (ID, edge details array)
      const [id, details] = nbr instanceof Array ? nbr : [nbr, []];
      return [id, {
        label: Array.from(new Set(details.map(d => d.dataset))).map(ds => datasets[ds]?.name || "<ds.name?>").join(", "),
        color: edgeColor(nbr),
        details,
      }];
    }))
  }, [adjacencyList, targetCategory]);

  return (
    <>
      <SigmaContainer className="graph-container" graph={MultiUndirectedGraph} settings={{
        // Allow container to have zero width initially (e.g., during CSS layout or hidden tabs)
        // Sigma will render properly once the container gets dimensions via ResizeObserver
        allowInvalidContainer: true,
        enableEdgeEvents: true,  // Sigma 3.0: replaces enableEdgeHoverEvents and enableEdgeClickEvents
        renderEdgeLabels: targetCategory !== "all",
        // Disable all camera zooming/scaling to prevent unwanted graph scaling
        enableCameraZooming: false,  // Sigma 3.0: replaces enableCameraWheel
        // Sigma 3.0: removed enableCameraDoubleClickZoom (now controlled by enableCameraZooming)
        minCameraRatio: 0.1,
        maxCameraRatio: 10,
        // Padding to prevent node clipping (max node radius is 48)
        // Reduced left padding to minimize LHS whitespace
        stagePadding: 20,
        // Enable z-index based rendering for edges
        zIndex: true,
        // Disable automatic label collision detection - we control labels via nodeReducer
        labelDensity: 0.001,  // Very low density = show more labels (default 1)
        labelGridCellSize: 1,  // Small grid cells = less aggressive collision detection
        // Disable label size threshold - render all labels regardless of node size
        labelRenderedSizeThreshold: -Infinity,  // Render labels even for very small nodes
        // Sigma 3.0: Custom label renderers using new setting names
        // CRITICAL: Must be set here in initial settings, not in useEffect
        // These replace Sigma 2.x labelRenderer and hoverRenderer
        defaultDrawNodeLabel: drawNodeLabel,
        defaultDrawNodeHover: drawHoveredNodeLabel,
        defaultDrawEdgeHover: drawHoveredEdgeLabel,
        // Use no-op program for node hover rendering to prevent WebGL circles from obscuring labels
        // The default node type is "circle", so we override its hover program with our no-op
        nodeHoverProgramClasses: { circle: NoOpNodeProgram },
      }} >
        <SigmaEdgeGraph
          nodes={sigmaNodes}
          adjacency={adjacency}
          columnCount={targetCategory === "all" ? 0 : columnCount}
          similarityColumn={similarityColumn}
          intermediateColumn={intermediateColumn}
          sortBySimilarity={sortBySimilarity}
          labelFilter={labelFilter}
          queryCategory={queryCategory}
          {...childProps}
        />
      </SigmaContainer>
    </>
  );
};

export default EdgeGraph;
